import { enumPresentation, requireKnownEnumValue } from "@/lib/enum";
import { RiskPolicyAction } from "@gram/client/models/components/riskpolicy.js";
import { Page } from "@/components/page-layout";
import { RequireScope } from "@/components/require-scope";
import { ShadowMCPDispositionPicker } from "@/components/shadow-mcp/ShadowMCPDispositionPicker";
import { ShadowMCPPolicyServerSelector } from "@/components/shadow-mcp/ShadowMCPPolicyServerSelector";
import {
  initialShadowMCPBlockedPolicyURLs,
  initialShadowMCPPolicyURLs,
  invalidateShadowMCPPolicyInventory,
  useShadowMCPPolicyInventory,
} from "@/components/shadow-mcp/useShadowMCPPolicyInventory";
import { Alert, AlertDescription } from "@/components/ui/Alert";
import { Card } from "@/components/ui/Card";
import { Checkbox } from "@/components/ui/Checkbox";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Slider } from "@/components/ui/Slider";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/Sheet";
import { Switch } from "@/components/ui/Switch";
import { TextArea } from "@/components/ui/Textarea";
import { SimpleTooltip } from "@/components/ui/Tooltip";
import { Text } from "@/components/ui/Text";
import { useRecentLabelOverride } from "@/components/command-palette/recentlyVisited";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "@/lib/featureFlags";
import { cn } from "@/lib/utils";
import { useProjectSlugForRequests } from "@/contexts/Sdk";
import { useRoutes } from "@/routes";
import { useProject } from "@/contexts/Auth";
import { useRiskCreatePolicyMutation } from "@gram/client/react-query/riskCreatePolicy.js";
import { useRiskCategories } from "@gram/client/react-query/riskCategories.js";
import { invalidateAllRiskListPolicies } from "@gram/client/react-query/riskListPolicies.js";
import { useRiskPoliciesUpdateMutation } from "@gram/client/react-query/riskPoliciesUpdate.js";
import { invalidateAllShadowMCPInventory } from "@gram/client/react-query/shadowMCPInventory.js";
import {
  invalidateAllRiskPoliciesGet,
  useRiskPoliciesGet,
} from "@gram/client/react-query/riskPoliciesGet.js";
import type { RiskMCPScope } from "@gram/client/models/components/riskmcpscope.js";
import { riskEvalsEvaluate } from "@gram/client/funcs/riskEvalsEvaluate.js";
import type { RiskPolicy } from "@gram/client/models/components/riskpolicy.js";
import type { RiskCategoryDefinition } from "@gram/client/models/components/riskcategorydefinition.js";
import type { RiskDetectionScope } from "@gram/client/models/components/riskdetectionscope.js";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Stack } from "@/components/ui/Stack";
import {
  keepPreviousData,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  Check,
  Code,
  Info,
  Loader2,
  Shield,
  ThumbsDown,
  ThumbsUp,
  TriangleAlert,
  X,
} from "lucide-react";
import {
  type ReactNode,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useParams } from "react-router";
import { toast } from "sonner";
import { useQueryState } from "nuqs";
import {
  ApprovedDomainsRequirement,
  PolicyDraftProvenance,
  PolicyStartChooser,
} from "./PolicyStart";
import {
  clearPolicyStartSession,
  supportsPolicyEditing,
  invalidEmailDomains,
  draftMCPScopeValue,
  type PolicyDraft,
  readPolicyDraft,
} from "./policy-draft";
import {
  parsePolicyNewPrefill,
  type PolicyNewPrefill,
} from "./policy-new-prefill";
import {
  isBlockingShadowMCPPolicy,
  isShadowMCPBlockConfiguration,
  SHADOW_MCP_ALLOW_ALL_LOCK_REASON,
  shadowMCPAllowAllPostureLocked,
  shadowMCPAllowedURLsForMutation,
  shadowMCPBlockedURLsForMutation,
  shadowMCPDecisionConflicts,
  shadowMCPSelectionBaselineForUpdate,
  shadowMCPSelectionIsDirty,
  shadowMCPSelectionIsInitialized,
  type ShadowMCPDecisionConflict,
  type ShadowMCPDisposition,
} from "./policy-shadow-mcp-setup";
import { SupersedeDecisionsDialog } from "./SupersedeDecisionsDialog";
import { PolicyMCPScopePicker } from "./PolicyMCPScopePicker";
import { PolicyScopeModeCards } from "./PolicyScopeModeCards";
import { Skeleton } from "@/components/ui/Skeleton";
import {
  initialPolicyMCPScopeValue,
  policyMCPScopeComplete,
  policyMCPScopePayload,
  policyScopeSummary,
  type PolicyMCPScopeValue,
  type PolicyScopeChoice,
} from "./policy-mcp-scope";
import { useGetHooksStatus } from "@gram/client/react-query/getHooksStatus.js";

import {
  DETECTION_RULES,
  ruleCategoryMeta,
  type DetectorMode,
  type PolicyAction,
  type RuleCategory,
} from "./policy-data";
import { CustomizeRulesSheet, RuleSelectList } from "./PolicyCenter";
import { DetectorCard } from "./DetectorCard";
import { ShadowAIPolicyEditor } from "./ShadowAIPolicyEditor";
import { isShadowAIPolicy } from "./policy-type";
import {
  ActionStep,
  PolicyHeader,
  SectionHeader,
  StepNav,
  StepperShell,
  SummaryRow,
} from "./policy-editor-shell";
import { type PolicyStep, useStepParam } from "./use-step-param";
import { builtInRuleDisabledReason } from "./policy-built-in-rule-exclusivity";
import {
  FLAG_ONLY_CATEGORIES,
  SCOPE_EXEMPT_CEL_EXAMPLES,
  SCOPE_INCLUDE_CEL_EXAMPLES,
  allCategories,
  categoriesToPayload,
  categoryLevelDetectors,
  mcpCompatibleAction,
  normalizeCategoriesForMode,
  parseApprovedEmailDomains,
  persistedCategories,
  personalDataCategories,
  pinnedHiddenRuleIds,
  policyToCategories,
} from "./policy-form";
import { useDetectorMode } from "./use-detector-mode";
import { decodeKindScope, encodeKindScope } from "./policy-scope";
import { SeverityBadge } from "./risk-ui";
import { CelExpressionField } from "./cel-field";
import { CelReferenceSheet } from "./cel-reference";
import { useCelEngine } from "./use-cel-engine";
import type { CelEngine, CelMessage } from "./cel-wasm";
import { useDetectionRulesStore } from "./detection-rules-data";
import { PROMPT_POLICY_TEMPLATES } from "./prompt-policy-templates";
import { SortBy, SortOrder } from "@gram/client/models/operations/listchats";
import { useListChats } from "@gram/client/react-query/listChats.js";
import { useLoadChat } from "@gram/client/react-query/loadChat.js";
import {
  invalidateAllRiskListEvalReviews,
  useRiskListEvalReviews,
} from "@gram/client/react-query/riskListEvalReviews.js";
import { useGramContext } from "@gram/client/react-query/_context.js";
import { unwrapAsync } from "@gram/client/types/fp.js";
import { useRiskSaveEvalReviewMutation } from "@gram/client/react-query/riskSaveEvalReview.js";
import { useRiskDeleteEvalReviewMutation } from "@gram/client/react-query/riskDeleteEvalReview.js";
import type { ChatOverview } from "@gram/client/models/components/chatoverview.js";
import type { PromptGuardrailEvalResult } from "@gram/client/models/components/promptguardrailevalresult.js";
import type { PromptGuardrailMessageVerdict } from "@gram/client/models/components/promptguardrailmessageverdict.js";
import {
  ChatTranscript,
  type RowContext,
  type TranscriptPagination,
} from "@/pages/chatLogs/ChatTranscript";
import {
  buildDisplayItems,
  buildTranscript,
} from "@/pages/chatLogs/transcript";
import { useChatTranscript } from "@/pages/chatLogs/useChatTranscript";
import { formatUsageCost } from "@/pages/chatLogs/claudeUsage";

const PROMPT_STEPS: PolicyStep[] = [
  {
    id: "guardrail",
    title: "Guardrail",
    description: "Describe the behavior to catch and tune the judge.",
  },
  {
    id: "scope",
    title: "Scope",
    description: "Choose which messages the judge evaluates.",
  },
  {
    id: "evaluate",
    title: "Evaluate",
    description: "Preview what it catches before choosing an action.",
  },
  {
    id: "action",
    title: "Action",
    description: "Decide what happens on a match, and who it applies to.",
  },
  {
    id: "review",
    title: "Review",
    description: "Confirm the configuration before creating the policy.",
  },
];

const STANDARD_STEPS: PolicyStep[] = [
  {
    id: "detect",
    title: "Detect",
    description: "Turn on detector categories and custom rules.",
  },
  {
    id: "scope",
    title: "Scope",
    description: "Narrow where the policy applies.",
  },
  {
    id: "action",
    title: "Action",
    description: "Choose the response and audience.",
  },
  {
    id: "review",
    title: "Review",
    description: "Confirm the configuration before creating the policy.",
  },
];

// Steps a draft from the intent screen fills in, tagged in the stepper.
const PROMPT_PREFILLED_STEPS: ReadonlySet<string> = new Set([
  "guardrail",
  "action",
]);
const STANDARD_PREFILLED_STEPS: ReadonlySet<string> = new Set([
  "detect",
  "action",
]);

const POLICY_ACTION_LABEL: Record<PolicyAction, string> = {
  flag: "Flag",
  warn: "Warn",
  block: "Block",
  quarantine: "Quarantine",
};

// Mock title summarization from the guardrail prompt (a real impl would use the
// LLM). Matches a known template name, else derives a short title.
function summarizeTitle(prompt: string): string {
  const trimmed = prompt.trim();
  const tmpl = PROMPT_POLICY_TEMPLATES.find((t) => t.prompt.trim() === trimmed);
  if (tmpl) return tmpl.name;
  const first = (trimmed.split("\n")[0] ?? "").replace(/[.!?]+$/, "").trim();
  const short = first.split(/\s+/).slice(0, 6).join(" ");
  return short ? short.charAt(0).toUpperCase() + short.slice(1) : "Untitled";
}

export default function PolicyDetail(): JSX.Element {
  const { policyId } = useParams<{ policyId: string }>();
  return (
    <RequireScope scope="org:admin" level="page">
      <PolicyDetailContent policyId={policyId!} />
    </RequireScope>
  );
}

/** The editor a stored policy opens in. Shadow AI is a presentation of the
 *  blocking Shadow MCP policy, so that policy opens the Shadow AI editor. */
export function PolicyEditorFor({
  policy,
}: {
  policy: RiskPolicy;
}): JSX.Element {
  if (!supportsPolicyEditing(policy))
    return (
      <Stack gap={6}>
        <Page.Section.Title>{policy.name}</Page.Section.Title>
        {policy.prompt && (
          <Text className="whitespace-pre-wrap">{policy.prompt}</Text>
        )}
        <dl className="grid gap-4 sm:grid-cols-2">
          <div>
            <dt className="text-eyebrow">Action</dt>
            <dd>
              {enumPresentation(POLICY_ACTION_LABEL, policy.action, "Unknown")}
            </dd>
          </div>
          <div>
            <dt className="text-eyebrow">Status</dt>
            <dd>{policy.enabled ? "Enabled" : "Disabled"}</dd>
          </div>
        </dl>
        <Alert>
          <AlertDescription>
            This policy uses settings this dashboard cannot safely edit. Refresh
            the page to check for an update.
          </AlertDescription>
        </Alert>
        <Button
          variant="secondary"
          className="self-start"
          onClick={() => window.location.reload()}
        >
          <Button.Text>Refresh page</Button.Text>
        </Button>
      </Stack>
    );
  if (policy.policyType === "prompt_based") {
    return <PromptPolicyEditor policy={policy} />;
  }
  if (isShadowAIPolicy(policy)) {
    return <ShadowAIPolicyEditor policy={policy} />;
  }
  return <StandardPolicyEditor policy={policy} />;
}

function PolicyDetailContent({ policyId }: { policyId: string }): JSX.Element {
  const location = useLocation();
  const { data: policy, isLoading } = useRiskPoliciesGet({ id: policyId });
  useRecentLabelOverride(
    location.pathname,
    policy ? `Guardrail · ${policy.name}` : undefined,
  );

  return (
    <Page>
      <Page.Header>
        <Page.Header.Breadcrumbs
          substitutions={
            policy ? { [policyId]: policy.name } : { [policyId]: "Policy" }
          }
        />
      </Page.Header>
      <Page.Body>
        {isLoading || !policy ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
          </div>
        ) : (
          <PolicyEditorFor policy={policy} />
        )}
      </Page.Body>
    </Page>
  );
}

// ── Create page (serves both standard and prompt policies) ───────────────────

export function PolicyNew(): JSX.Element {
  return (
    <RequireScope scope="org:admin" level="page">
      <PolicyNewContent />
    </RequireScope>
  );
}

function NewPolicyFrame({ children }: { children: ReactNode }): JSX.Element {
  return (
    <Page>
      <Page.Header>
        <Page.Header.Breadcrumbs />
      </Page.Header>
      <Page.Body>{children}</Page.Body>
    </Page>
  );
}

function PolicyNewContent(): JSX.Element {
  const [kind] = useQueryState("kind");
  const [category] = useQueryState("category");
  const location = useLocation();
  const draft = readPolicyDraft(location.state);
  // Read once: the editor seeds its state on mount and owns it from there.
  const [prefill] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    if (category && !params.has("category")) params.set("category", category);
    return parsePolicyNewPrefill(params);
  });
  if (kind === "standard") {
    return (
      <NewPolicyFrame>
        <StandardPolicyEditor
          policy={null}
          prefill={prefill}
          draft={draft?.policyType === "standard" ? draft : null}
        />
      </NewPolicyFrame>
    );
  }
  if (kind === "prompt") {
    return (
      <NewPolicyFrame>
        <PromptPolicyEditor
          policy={null}
          draft={draft?.policyType === "prompt_based" ? draft : null}
        />
      </NewPolicyFrame>
    );
  }
  if (kind === "shadow-ai") {
    return (
      <NewPolicyFrame>
        <ShadowAIPolicyEditor policy={null} />
      </NewPolicyFrame>
    );
  }
  return <PolicyStartChooser />;
}

// ── Prompt policy editor (stepped: Guardrail → Scope → Evaluate → Action → Review)
// One editor serves both create (policy === null) and edit.

function PromptPolicyEditor({
  policy,
  draft = null,
}: {
  policy: RiskPolicy | null;
  draft?: PolicyDraft | null;
}): JSX.Element {
  const routes = useRoutes();
  const gramProject = useProjectSlugForRequests();
  const queryClient = useQueryClient();
  const isCreate = policy === null;

  const [step, setStep] = useStepParam(PROMPT_STEPS);
  const [nameGenerating, setNameGenerating] = useState(false);

  // Editable guardrail definition, seeded from the loaded policy (edit), a
  // draft from the intent screen, or defaults (create). Kept local so the
  // author can iterate freely.
  const [name, setName] = useState(policy?.name ?? draft?.name ?? "");
  const [prompt, setPrompt] = useState(policy?.prompt ?? draft?.prompt ?? "");
  const [temperature, setTemperature] = useState(
    policy?.modelConfig?.temperature ?? 0,
  );
  const [failOpen, setFailOpen] = useState(
    policy?.modelConfig?.failOpen ?? true,
  );
  const [scopeOverrides, setScopeOverrides] = useState<
    Map<string, ScopeOverride>
  >(() => scopeOverridesFromPolicy(policy?.detectionScopes));
  const [mcpScope, setMcpScope] = useState<PolicyMCPScopeValue>(
    () =>
      (policy ? null : draftMCPScopeValue(draft)) ??
      initialPolicyMCPScopeValue(policy),
  );
  const [draftScopeNames, setDraftScopeNames] = useState<string[]>(
    policy ? [] : (draft?.mcpScope?.matchedNames ?? []),
  );
  const [action, setAction] = useState<PolicyAction>(() => {
    const initial = requireKnownEnumValue(
      RiskPolicyAction,
      policy?.action ?? draft?.action ?? "flag",
    );
    return mcpScope.mode === "mcp" ? mcpCompatibleAction(initial) : initial;
  });
  const updateMCPScope = (next: PolicyMCPScopeValue) => {
    setMcpScope(next);
    if (next.mode === "mcp") {
      setAction((current) => mcpCompatibleAction(current));
    }
  };
  const [audienceType, setAudienceType] = useState<"everyone" | "targeted">(
    policy?.audienceType === "targeted" ? "targeted" : "everyone",
  );
  const [audiencePrincipalUrns, setAudiencePrincipalUrns] = useState<
    Set<string>
  >(() =>
    policy?.audienceType === "targeted"
      ? new Set(policy.audiencePrincipalUrns ?? [])
      : new Set<string>(),
  );
  const [userMessage, setUserMessage] = useState(
    policy?.userMessage ?? draft?.userMessage ?? "",
  );
  const [score, setScore] = useState(policy?.score ?? draft?.score ?? 5);
  const [reviewVerdictFilter, setReviewVerdictFilter] =
    useState<EvalVerdict | null>(null);
  const mcpScopePayload = policyMCPScopePayload(mcpScope);

  const dirty =
    !!policy &&
    (name !== policy.name ||
      prompt !== (policy.prompt ?? "") ||
      temperature !== (policy.modelConfig?.temperature ?? 0) ||
      failOpen !== (policy.modelConfig?.failOpen ?? true) ||
      !sameScopeOverrides(
        scopeOverrides,
        scopeOverridesFromPolicy(policy.detectionScopes),
      ) ||
      !sameMCPScope(mcpScopePayload, policy.mcpScope ?? null) ||
      action !== (policy.action ?? "flag") ||
      userMessage !== (policy.userMessage ?? "") ||
      score !== (policy.score ?? 5) ||
      audienceType !==
        (policy.audienceType === "targeted" ? "targeted" : "everyone") ||
      !sameSet(
        audiencePrincipalUrns,
        new Set(
          policy.audienceType === "targeted"
            ? (policy.audiencePrincipalUrns ?? [])
            : [],
        ),
      ));

  const updateMutation = useRiskPoliciesUpdateMutation({
    onSuccess: () => {
      void invalidateAllRiskPoliciesGet(queryClient);
      void invalidateAllRiskListPolicies(queryClient);
      toast.success("Policy updated");
    },
  });
  const createMutation = useRiskCreatePolicyMutation({
    onSuccess: () => {
      clearPolicyStartSession(gramProject);
      void invalidateAllRiskListPolicies(queryClient);
      routes.policyCenter.goTo();
    },
  });
  const saving = updateMutation.isPending || createMutation.isPending;
  const evalReview = useEvalVerdicts(policy?.id ?? null);

  // Leaving the Guardrail step with no name yet: auto-summarize a title from
  // the guardrail (with a brief shimmer), like naming after "Continue".
  const handleStep = (next: number) => {
    if (
      step === 0 &&
      next !== 0 &&
      name.trim() === "" &&
      prompt.trim() !== ""
    ) {
      setName(summarizeTitle(prompt));
      setNameGenerating(true);
      setTimeout(() => setNameGenerating(false), 1200);
    }
    setStep(next);
  };

  const actionPayload = () => ({
    action,
    audienceType,
    audiencePrincipalUrns:
      audienceType === "targeted" ? [...audiencePrincipalUrns] : [],
  });

  // Blank name → the backend auto-generates one from the guardrail (mirrors
  // standard policies auto-naming from detectors).
  const autoName = name.trim() === "";
  const promptPolicyCategories = useMemo(
    () => new Set<RuleCategory>(["prompt_policy"]),
    [],
  );
  const mcpScopeValid = policyMCPScopeComplete(mcpScope);
  const detectionScopesPayloadForPrompt = () =>
    detectionScopesPayload(promptPolicyCategories, scopeOverrides);

  const save = () => {
    if (!policy) return;
    updateMutation.mutate({
      request: {
        updateRiskPolicyRequestBody: {
          id: policy.id,
          name: name.trim() || policy.name,
          prompt,
          modelConfig: {
            temperature,
            failOpen,
          },
          detectionScopes: detectionScopesPayloadForPrompt(),
          mcpScope: mcpScopePayload ?? { servers: [] },
          ...actionPayload(),
          userMessage,
          score,
          autoName,
        },
      },
    });
  };

  const create = () => {
    const detectionScopes = detectionScopesPayloadForPrompt();
    createMutation.mutate({
      request: {
        createRiskPolicyRequestBody: {
          policyType: "prompt_based",
          ...(autoName ? {} : { name: name.trim() }),
          enabled: true,
          prompt,
          modelConfig: { temperature, failOpen },
          ...(detectionScopes.length > 0 ? { detectionScopes } : {}),
          ...(mcpScopePayload === null ? {} : { mcpScope: mcpScopePayload }),
          ...actionPayload(),
          ...(userMessage.trim() ? { userMessage } : {}),
          score,
          autoName,
        },
      },
    });
  };

  const canCreate = prompt.trim().length > 0 && mcpScopeValid;

  // Stable guardrail snapshot for eval query keys. The replay uses the
  // prompt_policy category's effective detection scope (the override when
  // set, the recommendation otherwise) so eval verdicts match scan behavior.
  const categoriesQuery = useRiskCategories();
  const promptPolicyDef = categoriesQuery.data?.categories?.find(
    (c) => c.key === "prompt_policy",
  );
  const effectiveScope = scopeOverrides.get("prompt_policy") ?? {
    scopeInclude: promptPolicyDef?.recommendedScopeInclude ?? "",
    scopeExempt: promptPolicyDef?.recommendedScopeExempt ?? "",
  };
  const promptInspectRowsValid = inspectRowsAreValid(
    promptPolicyCategories,
    scopeOverrides,
    categoriesQuery.data?.categories,
    mcpScope.mode === "mcp",
  );
  const guardrail = useMemo<Guardrail>(
    () => ({
      prompt,
      temperature,
      failOpen,
      messageTypes: [],
      scopeInclude: effectiveScope.scopeInclude,
      scopeExempt: effectiveScope.scopeExempt,
    }),
    [
      prompt,
      temperature,
      failOpen,
      effectiveScope.scopeInclude,
      effectiveScope.scopeExempt,
    ],
  );

  const header = (
    <PolicyHeader
      kind="prompt"
      policy={policy}
      name={name}
      onNameChange={setName}
      dirty={dirty}
      saving={saving}
      actionDisabled={
        !mcpScopeValid || !promptInspectRowsValid || (isCreate && !canCreate)
      }
      onSubmit={() => save()}
      onCreate={create}
      nameGenerating={nameGenerating}
    />
  );

  return (
    <StepperShell
      header={header}
      provenance={draft ? <PolicyDraftProvenance draft={draft} /> : null}
      steps={PROMPT_STEPS}
      current={step}
      onStep={handleStep}
      prefilledStepIds={draft?.origin ? PROMPT_PREFILLED_STEPS : undefined}
    >
      {step === 0 && (
        <>
          <GuardrailCard prompt={prompt} onPromptChange={setPrompt} />
          <JudgeSection
            temperature={temperature}
            onTemperatureChange={setTemperature}
            failOpen={failOpen}
            onFailOpenChange={setFailOpen}
          />
        </>
      )}

      {step === 1 && (
        <ScopeStep
          selectedCategories={promptPolicyCategories}
          scopeOverrides={scopeOverrides}
          setScopeOverrides={setScopeOverrides}
          mcpScope={mcpScope}
          setMcpScope={updateMCPScope}
          action={action}
          hasStoredMcpScope={!!policy?.mcpScope}
          draftScopeNames={draftScopeNames}
          onClearDraftScope={() => {
            setDraftScopeNames([]);
            updateMCPScope(initialPolicyMCPScopeValue(null));
          }}
        />
      )}

      {step === 2 && (
        <EvalTuner
          guardrail={guardrail}
          onPromptChange={setPrompt}
          verdicts={evalReview.verdicts}
          setVerdict={evalReview.setVerdict}
          reviewVerdictFilter={reviewVerdictFilter}
          setReviewVerdictFilter={setReviewVerdictFilter}
        />
      )}

      {step === 3 && (
        <ActionStep
          action={action}
          setAction={setAction}
          audienceType={audienceType}
          setAudienceType={setAudienceType}
          audiencePrincipalUrns={audiencePrincipalUrns}
          setAudiencePrincipalUrns={setAudiencePrincipalUrns}
          userMessage={userMessage}
          setUserMessage={setUserMessage}
          score={score}
          setScore={setScore}
          mcpScoped={mcpScope.mode === "mcp"}
        />
      )}

      {step === 4 && (
        <PromptReview
          prompt={prompt}
          temperature={temperature}
          failOpen={failOpen}
          mcpScope={mcpScope}
          customizedScopeCount={scopeOverrides.size}
          action={action}
          score={score}
          audienceType={audienceType}
          audiencePrincipalCount={audiencePrincipalUrns.size}
          verdicts={evalReview.verdicts}
          activeVerdict={reviewVerdictFilter}
          onVerdictSelect={(verdict) => {
            setReviewVerdictFilter(verdict);
            handleStep(2);
          }}
        />
      )}

      <StepNav
        step={step}
        count={PROMPT_STEPS.length}
        onStep={handleStep}
        continueDisabled={step === 1 && !policyMCPScopeComplete(mcpScope)}
      />
    </StepperShell>
  );
}

// ── Guardrail hero (the prompt — the thing you iterate) ──────────────────────

function GuardrailCard({
  prompt,
  onPromptChange,
  rows = 12,
}: {
  prompt: string;
  onPromptChange: (v: string) => void;
  rows?: number;
}): JSX.Element {
  return (
    <Card>
      <SectionHeader description="Plain-language behavior the judge flags on each in-scope message." />
      <Stack gap={3}>
        <div className="flex flex-wrap gap-1.5">
          {PROMPT_POLICY_TEMPLATES.map((t) => (
            <button
              key={t.name}
              type="button"
              onClick={() => onPromptChange(t.prompt)}
              className="hover:bg-muted rounded-full border px-2.5 py-1 text-xs transition-colors"
            >
              {t.name}
            </button>
          ))}
        </div>
        <TextArea
          value={prompt}
          onChange={onPromptChange}
          rows={rows}
          placeholder="e.g. Flag any tool call that issues a refund without a prior authorization step."
        />
      </Stack>
    </Card>
  );
}

// ── Judge section (temperature · fail behavior) ──────────────────────────────

function JudgeSection({
  temperature,
  onTemperatureChange,
  failOpen,
  onFailOpenChange,
}: {
  temperature: number;
  onTemperatureChange: (v: number) => void;
  failOpen: boolean;
  onFailOpenChange: (v: boolean) => void;
}): JSX.Element {
  return (
    <Card>
      <SectionHeader
        title="Judge"
        description="How the judge evaluates each in-scope message and how it behaves under error."
      />
      <Stack gap={8}>
        {/* Temperature */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Text small>Temperature</Text>
            <Text small mono>
              {temperature.toFixed(1)}
            </Text>
          </div>
          <Text small muted>
            Lower is more deterministic and repeatable; higher allows more
            nuanced judgment but less consistent results.
          </Text>
          <div className="pt-3">
            <Slider
              value={temperature}
              onChange={(v) => onTemperatureChange(Math.max(0, Math.min(1, v)))}
              min={0}
              max={1}
              step={0.1}
              ticks={[0, 0.5, 1]}
            />
          </div>
        </div>

        {/* On judge error */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Text small>On judge error</Text>
            <Stack direction="horizontal" gap={2} align="center">
              <Text small muted>
                {failOpen ? "Fail open" : "Fail closed"}
              </Text>
              <Switch checked={failOpen} onCheckedChange={onFailOpenChange} />
            </Stack>
          </div>
          <Text small muted>
            If the judge times out or errors, fail open lets the message through
            (no false blocks); fail closed blocks it (stricter, but can
            interrupt legitimate traffic).
          </Text>
        </div>
      </Stack>
    </Card>
  );
}

// ── Scope section (message types · include/exempt CEL) ───────────────────────

// Shared scope step for both policy editors.
function ScopeStep({
  selectedCategories,
  scopeOverrides,
  setScopeOverrides,
  mcpScope,
  setMcpScope,
  action,
  hasStoredMcpScope,
  draftScopeNames,
  onClearDraftScope,
}: {
  selectedCategories: Set<RuleCategory>;
  scopeOverrides: Map<string, ScopeOverride>;
  setScopeOverrides: (next: Map<string, ScopeOverride>) => void;
  mcpScope: PolicyMCPScopeValue;
  setMcpScope: (next: PolicyMCPScopeValue) => void;
  action: PolicyAction;
  hasStoredMcpScope: boolean;
  /** Server names a description-based draft pre-selected, shown so the
   *  administrator can see why the scope is already set. */
  draftScopeNames: string[];
  onClearDraftScope: () => void;
}): JSX.Element {
  const mcpScopeFlag = useFeatureFlag(FEATURE_FLAGS.mcpScopedPolicies);
  const gramProject = useProjectSlugForRequests();
  // Configuration only (hooks-scoped keys, inference hooks), never traffic,
  // so it is cheap enough to read on every policy edit.
  const hooksStatus = useGetHooksStatus({ gramProject }, undefined, {
    throwOnError: false,
    staleTime: 5 * 60 * 1000,
  });
  const mcpScoped = mcpScope.mode === "mcp";
  const chosen = mcpScope.mode !== "unset";
  // Only a successful read offers Client sessions. A failed read offers no
  // cards and asks for a retry: guessing either way would let the author save
  // a policy whose scope was never verified.
  const sessionsAvailable = hooksStatus.data?.configured ?? false;
  // Fail closed while the flag loads or is unavailable. A stored scope or an
  // in-progress MCP draft keeps the MCP option so the form never strands the
  // user in MCP mode without the control to change it.
  const mcpAvailable =
    mcpScopeFlag.status === "enabled" || hasStoredMcpScope || mcpScoped;
  const settled = hooksStatus.isSuccess && mcpScopeFlag.status !== "loading";
  // A single available option is taken without a click: an org without hooks
  // goes straight to MCP scope, an org without the MCP flag to client
  // sessions. Waits for both signals so a value still loading never pre-empts
  // the author's choice.
  useEffect(() => {
    if (!settled || sessionsAvailable === mcpAvailable) return;
    const only: PolicyScopeChoice = mcpAvailable ? "mcp" : "everywhere";
    if (mcpScope.mode !== only) setMcpScope({ ...mcpScope, mode: only });
  }, [settled, sessionsAvailable, mcpAvailable, mcpScope, setMcpScope]);
  return (
    <Card>
      <Stack gap={6}>
        {hooksStatus.isError ? (
          <div className="flex items-center gap-3">
            <Text small muted>
              Could not check whether hooks are configured.
            </Text>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void hooksStatus.refetch()}
            >
              <Button.Text>Retry</Button.Text>
            </Button>
          </div>
        ) : null}
        {settled ? (
          <PolicyScopeModeCards
            value={mcpScope.mode}
            sessionsAvailable={sessionsAvailable}
            mcpAvailable={mcpAvailable}
            onChange={(mode) => setMcpScope({ ...mcpScope, mode })}
            // The dialog may have minted a hooks key; the cached status would
            // otherwise keep Client sessions hidden until staleTime elapses.
            onHooksSetupClosed={() => void hooksStatus.refetch()}
          />
        ) : null}
        {!settled && !hooksStatus.isError ? (
          <Skeleton className="h-28 w-full" />
        ) : null}
        {settled && !chosen && (sessionsAvailable || mcpAvailable) ? (
          <Text small muted>
            Choose where this policy applies to continue.
          </Text>
        ) : null}
        {mcpScoped && draftScopeNames.length > 0 ? (
          <div className="flex items-center justify-between gap-4 border px-4 py-3 text-sm">
            <span>
              Set from your description, which names{" "}
              {draftScopeNames.join(", ")}.
            </span>
            <Button
              type="button"
              size="xs"
              variant="tertiary"
              onClick={onClearDraftScope}
            >
              Clear
            </Button>
          </div>
        ) : null}
        {mcpScoped ? (
          <PolicyMCPScopePicker
            value={mcpScope}
            onChange={setMcpScope}
            action={action}
          />
        ) : null}
        {chosen && selectedCategories.size === 0 ? (
          <Text small muted>
            Scope options appear here once you enable a detector.
          </Text>
        ) : null}
        {mcpScoped && !mcpScope.allServers && mcpScope.servers.length === 0 ? (
          <Text small className="text-destructive">
            Select at least one MCP server or apply the policy to all servers.
          </Text>
        ) : null}
        {mcpScoped && selectedCategories.has("account_identity") ? (
          <Text small className="text-destructive">
            Session-scoped account identity detection cannot be limited to MCP
            tool traffic.
          </Text>
        ) : null}
        {mcpScoped && selectedCategories.has("shadow_mcp") ? (
          <Text small className="text-destructive">
            Shadow MCP detection watches unmanaged servers, so it cannot be
            limited to selected MCP servers.
          </Text>
        ) : null}
        {chosen ? (
          <InspectMatrix
            selectedCategories={selectedCategories}
            scopeOverrides={scopeOverrides}
            setScopeOverrides={setScopeOverrides}
            mcpScoped={mcpScoped}
          />
        ) : null}
      </Stack>
    </Card>
  );
}

function InspectMatrix({
  selectedCategories,
  scopeOverrides,
  setScopeOverrides,
  mcpScoped,
}: {
  selectedCategories: Set<RuleCategory>;
  scopeOverrides: Map<string, ScopeOverride>;
  setScopeOverrides: (next: Map<string, ScopeOverride>) => void;
  mcpScoped: boolean;
}): JSX.Element | null {
  const categoriesQuery = useRiskCategories(undefined, undefined, {
    throwOnError: false,
  });
  const rows = useMemo(() => {
    if (!categoriesQuery.data?.categories) return [];
    return categoriesQuery.data.categories.filter((category) =>
      selectedCategories.has(category.key as RuleCategory),
    );
  }, [categoriesQuery.data?.categories, selectedCategories]);

  if (categoriesQuery.isLoading) {
    return (
      <Text small muted className="flex items-center gap-2">
        <Loader2 className="size-4 animate-spin" />
        Loading inspection surfaces...
      </Text>
    );
  }
  if (categoriesQuery.isError) {
    return (
      <div className="flex items-center gap-3">
        <Text small muted>
          Failed to load inspection surfaces.
        </Text>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => void categoriesQuery.refetch()}
        >
          <Button.Text>Retry</Button.Text>
        </Button>
      </div>
    );
  }
  if (rows.length === 0) return null;

  const edited = rows.some(
    (category) =>
      category.recommendedScopeApplicable && scopeOverrides.has(category.key),
  );
  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <Label className="text-sm font-medium">Inspect</Label>
        <p className="text-muted-foreground text-xs">
          Choose which traffic each detector inspects.
        </p>
        {mcpScoped ? (
          <p className="text-muted-foreground text-xs">
            Tool responses are stored now. Response inspection applies once MCP
            response scanning ships.
          </p>
        ) : null}
      </div>
      <div className="border-border overflow-x-auto border">
        <div className="min-w-[720px]">
          <div className="bg-muted/35 text-muted-foreground grid grid-cols-[minmax(15rem,1.4fr)_repeat(4,minmax(6.5rem,0.65fr))] border-b text-xs">
            <div className="px-3 py-2 font-medium">Detector</div>
            <InspectHeader title="User" subtitle="Message" />
            <InspectHeader title="Tool" subtitle="Request" />
            <InspectHeader title="Tool" subtitle="Response" />
            <InspectHeader title="Assistant" subtitle="Message" />
          </div>
          {rows.map((category) => (
            <InspectRow
              key={category.key}
              category={category}
              override={scopeOverrides.get(category.key)}
              onOverrideChange={(override) => {
                const next = new Map(scopeOverrides);
                if (override === null) next.delete(category.key);
                else next.set(category.key, override);
                setScopeOverrides(next);
              }}
              mcpScoped={mcpScoped}
            />
          ))}
          {edited ? (
            <div className="border-t px-3 py-2 text-xs">
              <span className="text-muted-foreground">
                Edited from recommendations.
              </span>{" "}
              <button
                type="button"
                className="text-foreground underline"
                onClick={() => {
                  const next = new Map(scopeOverrides);
                  for (const category of rows) next.delete(category.key);
                  setScopeOverrides(next);
                }}
              >
                Reset to recommended
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function InspectHeader({
  title,
  subtitle,
}: {
  title: string;
  subtitle: string;
}): JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center px-2 py-2 text-center">
      <span className="text-foreground font-medium">{title}</span>
      <span>{subtitle}</span>
    </div>
  );
}

// The four message surfaces a detection scope selects over, in transcript
// order. Chip editing serializes back to canonical kind expressions.
const SCOPE_SURFACES = [
  { kind: "user_message", label: "User" },
  { kind: "tool_request", label: "Tool requests" },
  { kind: "tool_response", label: "Tool responses" },
  { kind: "assistant_message", label: "Assistant" },
] as const;
type ScopeSurfaceKind = (typeof SCOPE_SURFACES)[number]["kind"];
const ALL_SURFACE_KINDS: ScopeSurfaceKind[] = SCOPE_SURFACES.map((s) => s.kind);

// Parses an expression made solely of `kind == "..."` terms ORed together;
// null for anything else (a real CEL scope the chips cannot represent).
function kindsFromExpr(expr: string): Set<ScopeSurfaceKind> | null {
  const trimmed = expr.trim();
  const out = new Set<ScopeSurfaceKind>();
  if (trimmed === "") return out;

  const decoded = decodeKindScope(trimmed);
  if (decoded) return new Set(decoded);

  for (const part of trimmed.split("||")) {
    const match = /^\(?\s*kind\s*==\s*"(\w+)"\s*\)?$/.exec(part.trim());
    const kind = match?.[1] as ScopeSurfaceKind | undefined;
    if (!kind || !ALL_SURFACE_KINDS.includes(kind)) return null;
    out.add(kind);
  }
  return out;
}

// The surfaces a scope admits, or null when it is not a pure surface
// expression. An empty include means every surface.
function surfacesFromScope(
  include: string,
  exempt: string,
): Set<ScopeSurfaceKind> | null {
  const included = kindsFromExpr(include);
  const exempted = kindsFromExpr(exempt);
  if (included === null || exempted === null) return null;
  const base =
    included.size === 0
      ? new Set<ScopeSurfaceKind>(ALL_SURFACE_KINDS)
      : included;
  return new Set([...base].filter((kind) => !exempted.has(kind)));
}

type SurfaceState = "in" | "out" | "conditional";

// Canonical probe messages per surface. tool_request gets a write probe and an
// all-read-only probe so tool-conditional scopes (e.g. a read-only allowlist)
// register as "conditional" rather than a hard yes/no.
const SURFACE_PROBES: Record<ScopeSurfaceKind, CelMessage[]> = {
  user_message: [{ type: "user_message", content: "sample text" }],
  assistant_message: [{ type: "assistant_message", content: "sample text" }],
  tool_response: [{ type: "tool_response", content: "sample text" }],
  tool_request: [
    {
      type: "tool_request",
      content: "",
      tools: [
        { name: "Bash", server: "", function: "Bash", args: '{"command":"x"}' },
      ],
    },
    {
      type: "tool_request",
      content: "",
      tools: [{ name: "Read", server: "", function: "Read", args: "{}" }],
    },
  ],
};

// Per-surface footprint for scopes the chips cannot represent exactly
// (granular recommendations), derived by evaluating the scope against the
// probes: in scope for every probe, none, or only some ("conditional").
function surfaceStatesFromProbes(
  engine: CelEngine,
  include: string,
  exempt: string,
): Record<ScopeSurfaceKind, SurfaceState> | null {
  const inc = include.trim();
  const exc = exempt.trim();
  if (inc !== "" && !engine.compile(inc).ok) return null;
  if (exc !== "" && !engine.compile(exc).ok) return null;
  const out = {} as Record<ScopeSurfaceKind, SurfaceState>;
  for (const kind of ALL_SURFACE_KINDS) {
    const verdicts: boolean[] = [];
    for (const probe of SURFACE_PROBES[kind]) {
      let inScope = true;
      if (inc !== "") {
        const result = engine.evalDetection(inc, probe);
        if (!result.ok) return null;
        inScope = result.matched;
      }
      if (inScope && exc !== "") {
        const result = engine.evalDetection(exc, probe);
        if (!result.ok) return null;
        if (result.matched) inScope = false;
      }
      verdicts.push(inScope);
    }
    out[kind] = verdicts.every(Boolean)
      ? "in"
      : verdicts.some(Boolean)
        ? "conditional"
        : "out";
  }
  return out;
}

// Forces one surface fully in or out of a scope the chips cannot express
// exactly, by wrapping the existing predicates rather than rewriting them:
// the rest of the scope's conditions are preserved verbatim.
function scopeWithSurface(
  scope: ScopeOverride,
  kind: ScopeSurfaceKind,
  on: boolean,
): ScopeOverride {
  const include = scope.scopeInclude.trim();
  const exempt = scope.scopeExempt.trim();
  const kindEq = `kind == "${kind}"`;
  const kindNe = `kind != "${kind}"`;
  if (on) {
    return {
      scopeInclude: include === "" ? "" : `(${include}) || ${kindEq}`,
      scopeExempt: exempt === "" ? "" : `(${exempt}) && ${kindNe}`,
    };
  }
  return {
    scopeInclude: include === "" ? "" : `(${include}) && ${kindNe}`,
    scopeExempt: exempt === "" ? kindEq : `(${exempt}) || ${kindEq}`,
  };
}

// Canonical scope for a surface set: every surface = unrestricted, one
// missing = exempt it, otherwise include the chosen surfaces.
function scopeFromSurfaces(surfaces: Set<ScopeSurfaceKind>): ScopeOverride {
  if (surfaces.size >= ALL_SURFACE_KINDS.length) {
    return { scopeInclude: "", scopeExempt: "" };
  }
  const missing = ALL_SURFACE_KINDS.filter((kind) => !surfaces.has(kind));
  if (missing.length === 1) {
    return { scopeInclude: "", scopeExempt: `kind == "${missing[0]}"` };
  }
  return {
    scopeInclude: encodeKindScope(
      ALL_SURFACE_KINDS.filter((kind) => surfaces.has(kind)),
    ),
    scopeExempt: "",
  };
}

function inspectRowsAreValid(
  selectedCategories: ReadonlySet<RuleCategory>,
  scopeOverrides: ReadonlyMap<string, ScopeOverride>,
  categories: RiskCategoryDefinition[] | undefined,
  mcpScoped: boolean,
): boolean {
  if (!categories) return true;
  return [...selectedCategories].every((key) => {
    const category = categories.find((candidate) => candidate.key === key);
    if (!category) return true;
    if (!category.recommendedScopeApplicable) return true;
    const scope = scopeOverrides.get(key) ?? {
      scopeInclude: category.recommendedScopeInclude,
      scopeExempt: category.recommendedScopeExempt,
    };
    const surfaces = surfacesFromScope(scope.scopeInclude, scope.scopeExempt);
    if (!surfaces) return true;
    if (!mcpScoped) return surfaces.size > 0;
    return surfaces.has("tool_request") || surfaces.has("tool_response");
  });
}

function InspectRow({
  category,
  override,
  onOverrideChange,
  mcpScoped,
}: {
  category: RiskCategoryDefinition;
  override: ScopeOverride | undefined;
  onOverrideChange: (override: ScopeOverride | null) => void;
  mcpScoped: boolean;
}): JSX.Element {
  const [celOpen, setCelOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const engineState = useCelEngine();
  const engine = engineState.status === "ready" ? engineState.engine : null;
  if (!category.recommendedScopeApplicable) {
    return (
      <div className="border-border border-b last:border-b-0">
        <div className="grid grid-cols-[minmax(15rem,1.4fr)_repeat(4,minmax(6.5rem,0.65fr))]">
          <div className="flex min-w-0 items-center gap-2 px-3 py-3">
            <Text small className="truncate font-medium">
              {category.label}
            </Text>
            <ScopeRationaleHint
              rationale={category.recommendedScopeRationale}
            />
          </div>
          <div className="border-border text-muted-foreground col-span-4 flex items-center border-l px-3 py-3 text-xs">
            Inspection surfaces do not apply to this detector.
          </div>
        </div>
      </div>
    );
  }

  const activeScope = override ?? {
    scopeInclude: category.recommendedScopeInclude,
    scopeExempt: category.recommendedScopeExempt,
  };
  const activeSurfaces = surfacesFromScope(
    activeScope.scopeInclude,
    activeScope.scopeExempt,
  );
  const probedStates =
    activeSurfaces === null && engine
      ? surfaceStatesFromProbes(
          engine,
          activeScope.scopeInclude,
          activeScope.scopeExempt,
        )
      : null;
  const surfaceState = (kind: ScopeSurfaceKind): SurfaceState => {
    if (activeSurfaces) return activeSurfaces.has(kind) ? "in" : "out";
    return probedStates?.[kind] ?? "conditional";
  };
  const editableKinds = mcpScoped
    ? (["tool_request", "tool_response"] as ScopeSurfaceKind[])
    : ALL_SURFACE_KINDS;
  const activeCount = editableKinds.filter(
    (kind) => surfaceState(kind) !== "out",
  ).length;
  const invalid = activeCount === 0;

  const toggleSurface = (kind: ScopeSurfaceKind) => {
    const state = surfaceState(kind);
    const nextOn = state !== "in";
    if (!nextOn && activeCount <= 1) return;
    if (activeSurfaces) {
      const next = new Set(activeSurfaces);
      if (nextOn) next.add(kind);
      else next.delete(kind);
      onOverrideChange(scopeFromSurfaces(next));
      return;
    }
    onOverrideChange(scopeWithSurface(activeScope, kind, nextOn));
  };

  return (
    <div className="border-border border-b last:border-b-0">
      <div className="grid grid-cols-[minmax(15rem,1.4fr)_repeat(4,minmax(6.5rem,0.65fr))]">
        <div className="flex min-w-0 items-center gap-2 px-3 py-3">
          <span className="min-w-0">
            <Text small className="truncate font-medium">
              {category.label}
            </Text>
            {invalid ? (
              <span className="text-destructive block text-xs">
                Select at least one inspection surface.
              </span>
            ) : null}
          </span>
          <ScopeRationaleHint rationale={category.recommendedScopeRationale} />
          {!mcpScoped ? (
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground ml-auto flex shrink-0 items-center gap-1 text-xs underline"
              onClick={() => setCelOpen((open) => !open)}
            >
              <Code className="size-3" />
              CEL
            </button>
          ) : null}
        </div>
        {SCOPE_SURFACES.map(({ kind, label }) => {
          const unavailable =
            mcpScoped && kind !== "tool_request" && kind !== "tool_response";
          if (unavailable) {
            return (
              <SimpleTooltip key={kind} tooltip="Not part of an MCP call">
                <div
                  aria-label={`${label} is not part of an MCP call`}
                  className="border-border text-muted-foreground flex items-center justify-center border-l border-dashed px-2 py-3 text-xs"
                >
                  -
                </div>
              </SimpleTooltip>
            );
          }
          const state = surfaceState(kind);
          const checkbox = (
            <Checkbox
              aria-label={`${category.label}: ${label}`}
              checked={
                state === "conditional" ? "indeterminate" : state === "in"
              }
              onCheckedChange={() => toggleSurface(kind)}
            />
          );
          return (
            <div
              key={kind}
              className="border-border flex items-center justify-center border-l px-2 py-3"
            >
              {state === "conditional" ? (
                <SimpleTooltip tooltip="Only some traffic on this surface is inspected.">
                  {checkbox}
                </SimpleTooltip>
              ) : (
                checkbox
              )}
            </div>
          );
        })}
      </div>
      {celOpen && !mcpScoped ? (
        <div className="bg-muted/20 border-t px-3 py-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 space-y-2">
              <Text small className="font-medium">
                Generated CEL scope
              </Text>
              {activeScope.scopeInclude.trim() === "" &&
              activeScope.scopeExempt.trim() === "" ? (
                <code className="text-xs">All message surfaces</code>
              ) : (
                <RecommendedScopeCode
                  include={activeScope.scopeInclude}
                  exempt={activeScope.scopeExempt}
                />
              )}
              <Text small muted>
                Checkbox changes update this expression. Editing it replaces the
                generated scope.
              </Text>
            </div>
            <div className="flex shrink-0 items-center gap-3">
              <CelReferenceSheet />
              <button
                type="button"
                className="text-foreground text-xs underline"
                onClick={() => {
                  if (override === undefined) {
                    onOverrideChange({ ...activeScope });
                  }
                  setEditing((open) => !open);
                }}
              >
                {editing ? "Close editor" : "Edit expression"}
              </button>
            </div>
          </div>
          {editing ? (
            <div className="mt-4 space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">
                  Detect on messages matching
                </Label>
                <CelExpressionField
                  value={activeScope.scopeInclude}
                  onChange={(value) =>
                    onOverrideChange({
                      ...activeScope,
                      scopeInclude: value,
                    })
                  }
                  examples={SCOPE_INCLUDE_CEL_EXAMPLES}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">
                  Exempt messages matching
                </Label>
                <CelExpressionField
                  value={activeScope.scopeExempt}
                  onChange={(value) =>
                    onOverrideChange({
                      ...activeScope,
                      scopeExempt: value,
                    })
                  }
                  examples={SCOPE_EXEMPT_CEL_EXAMPLES}
                />
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function ScopeRationaleHint({
  rationale,
}: {
  rationale: string;
}): JSX.Element | null {
  if (rationale.trim() === "") return null;
  return (
    <SimpleTooltip tooltip={rationale}>
      <Info className="text-muted-foreground size-3.5 shrink-0" />
    </SimpleTooltip>
  );
}

function RecommendedScopeCode({
  include,
  exempt,
}: {
  include: string;
  exempt: string;
}): JSX.Element {
  return (
    <div className="space-y-1.5">
      {include.trim() !== "" && (
        <RecommendedScopeCodeLine label="Include" expr={include} />
      )}
      {exempt.trim() !== "" && (
        <RecommendedScopeCodeLine label="Exempt" expr={exempt} />
      )}
    </div>
  );
}

function RecommendedScopeCodeLine({
  label,
  expr,
}: {
  label: string;
  expr: string;
}): JSX.Element {
  return (
    <div className="grid gap-1 sm:grid-cols-[4.5rem_minmax(0,1fr)]">
      <span className="text-muted-foreground text-xs">{label}</span>
      <pre className="bg-muted/50 text-muted-foreground overflow-x-auto px-2 py-1 font-mono text-[11px] leading-tight whitespace-pre">
        {expr}
      </pre>
    </div>
  );
}

// A category with an entry here has its recommendation replaced by the
// user-authored scope; both fields empty scans every message surface.
type ScopeOverride = { scopeInclude: string; scopeExempt: string };

function scopeOverridesFromPolicy(
  scopes: RiskDetectionScope[] | undefined,
): Map<string, ScopeOverride> {
  return new Map(
    (scopes ?? []).map((s) => [
      s.category,
      { scopeInclude: s.scopeInclude ?? "", scopeExempt: s.scopeExempt ?? "" },
    ]),
  );
}

function sameScopeOverrides(
  a: Map<string, ScopeOverride>,
  b: Map<string, ScopeOverride>,
): boolean {
  if (a.size !== b.size) return false;
  for (const [category, override] of a) {
    const other = b.get(category);
    if (
      !other ||
      other.scopeInclude !== override.scopeInclude ||
      other.scopeExempt !== override.scopeExempt
    ) {
      return false;
    }
  }
  return true;
}

function scopeSummaryText(
  mcpScope: PolicyMCPScopeValue,
  customizedScopeCount: number,
): string {
  const inspect =
    customizedScopeCount > 0
      ? `recommended scopes (${customizedScopeCount} customized)`
      : "recommended scopes";
  return `${policyScopeSummary(mcpScope)} · ${inspect}`;
}

function detectionScopesPayload(
  selectedCategories: Set<RuleCategory>,
  overrides: Map<string, ScopeOverride>,
  mode: DetectorMode = "presidio",
): RiskDetectionScope[] {
  const persistedFor = persistedCategories(selectedCategories, mode);
  return [...overrides]
    .filter(([category]) => persistedFor.has(category as RuleCategory))
    .map(([category, override]) => ({
      category,
      ...(override.scopeInclude.trim()
        ? { scopeInclude: override.scopeInclude.trim() }
        : {}),
      ...(override.scopeExempt.trim()
        ? { scopeExempt: override.scopeExempt.trim() }
        : {}),
    }));
}

// ── Sensitivity section (Presidio match-confidence threshold) ────────────────
// The minimum confidence a Presidio PII match must clear to be flagged. Applies
// to every Presidio-backed detector in a standard policy and is only persisted
// while at least one such category is active (see `presidioActive` in the
// editor). Non-Presidio policies don't carry a stray threshold. Rendered inside
// the Detect step, and only while a confidence-scored category is on.
const PRESIDIO_THRESHOLD_MIN = 0;
const PRESIDIO_THRESHOLD_MAX = 1;
const PRESIDIO_THRESHOLD_STEP = 0.05;
const PRESIDIO_THRESHOLD_TICKS = [0, 0.25, 0.5, 0.75, 1];
const DEFAULT_PRESIDIO_THRESHOLD = 0.5;
const EMPTY_SHADOW_MCP_URLS: ReadonlySet<string> = new Set<string>();

// Shown on personal-data policies while the LLM analyzer flag is on for the
// organization. The stored Presidio settings stay on the policy (turning the
// flag off restores them) but do not drive detection meanwhile.
export const LLM_ANALYZER_NOTICE =
  "Entity selection, detection sensitivity and entity-type exclusions are not applied while the LLM analyzer is enabled for this organization. The model decides which personal-data category applies per finding.";

function LlmAnalyzerNotice(): JSX.Element {
  return (
    <Alert variant="info" alignTop>
      <AlertDescription>{LLM_ANALYZER_NOTICE}</AlertDescription>
    </Alert>
  );
}

function SensitivitySection({
  threshold,
  setThreshold,
}: {
  threshold: number;
  setThreshold: React.Dispatch<React.SetStateAction<number>>;
}): JSX.Element {
  return (
    <Card>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium">Detection sensitivity</Label>
          <Text small mono>
            {threshold.toFixed(2)}
            {threshold === DEFAULT_PRESIDIO_THRESHOLD ? " · default" : ""}
          </Text>
        </div>
        <Text small muted>
          Minimum confidence a match must clear to be flagged, from{" "}
          {PRESIDIO_THRESHOLD_MIN} to {PRESIDIO_THRESHOLD_MAX}. Higher means
          fewer false positives but may miss borderline matches. Applies to all
          confidence-scored detectors in this policy.
        </Text>
        <div className="pt-3">
          <Slider
            value={threshold}
            onChange={(v) =>
              setThreshold(
                Math.max(
                  PRESIDIO_THRESHOLD_MIN,
                  Math.min(PRESIDIO_THRESHOLD_MAX, Math.round(v * 20) / 20),
                ),
              )
            }
            min={PRESIDIO_THRESHOLD_MIN}
            max={PRESIDIO_THRESHOLD_MAX}
            step={PRESIDIO_THRESHOLD_STEP}
            ticks={PRESIDIO_THRESHOLD_TICKS}
          />
        </div>
      </div>
    </Card>
  );
}

// ── Evaluate step: prompt-tuning workbench ────────────────────────────────────

type EvalVerdict = "correct" | "false_positive" | "missed";
type JudgeAgreement = "agree" | "disagree";
type EvalMatchFilter = "all" | "flagged" | "clean";

type Guardrail = {
  prompt: string;
  temperature: number;
  failOpen: boolean;
  messageTypes: string[];
  scopeInclude: string;
  scopeExempt: string;
};

const EVAL_SESSION_LIMIT = 8;

function evalRequestBody(guardrail: Guardrail, chatId: string) {
  return {
    evaluatePromptGuardrailRequestBody: {
      chatId,
      prompt: guardrail.prompt,
      modelConfig: {
        temperature: guardrail.temperature,
        failOpen: guardrail.failOpen,
      },
      messageTypes: guardrail.messageTypes.length
        ? guardrail.messageTypes
        : undefined,
      scopeInclude: guardrail.scopeInclude.trim() || undefined,
      scopeExempt: guardrail.scopeExempt.trim() || undefined,
    },
  };
}

function evalQueryKey(guardrail: Guardrail, chatId: string) {
  return [
    "@gram/client",
    "evals",
    "evaluate",
    evalRequestBody(guardrail, chatId),
  ] as const;
}

function isFailOpenCleanEval(result: PromptGuardrailEvalResult): boolean {
  return (
    !result.flagged &&
    result.verdicts.length > 0 &&
    result.verdicts.every((v) => v.totalTokens === 0)
  );
}

function usePromptGuardrailEval(
  guardrail: Guardrail,
  chatId: string,
  enabled: boolean,
) {
  const client = useGramContext();
  const request = useMemo(
    () => evalRequestBody(guardrail, chatId),
    [guardrail, chatId],
  );

  // Key by POST body; the generated hook does not.
  return useQuery({
    queryKey: evalQueryKey(guardrail, chatId),
    queryFn: ({ signal }) =>
      unwrapAsync(riskEvalsEvaluate(client, request, undefined, { signal })),
    enabled,
    ...JUDGE_QUERY_OPTIONS,
  });
}

function guardrailEvalKey(guardrail: Guardrail): string {
  return JSON.stringify({
    prompt: guardrail.prompt,
    temperature: guardrail.temperature,
    failOpen: guardrail.failOpen,
    messageTypes: guardrail.messageTypes,
    scopeInclude: guardrail.scopeInclude,
    scopeExempt: guardrail.scopeExempt,
  });
}

const JUDGE_QUERY_OPTIONS = {
  // Cache completed judge calls by guardrail and chat.
  staleTime: Infinity,
  gcTime: 5 * 60 * 1000,
  refetchOnWindowFocus: false,
} as const;

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

function useEvalVerdicts(policyId: string | null): {
  verdicts: Map<string, EvalVerdict>;
  setVerdict: (chatId: string, verdict: EvalVerdict) => void;
} {
  const queryClient = useQueryClient();
  const [localVerdicts, setLocalVerdicts] = useState<Map<string, EvalVerdict>>(
    new Map(),
  );
  const listQuery = useRiskListEvalReviews(
    { policyId: policyId ?? "" },
    undefined,
    {
      enabled: !!policyId,
    },
  );
  const saveMutation = useRiskSaveEvalReviewMutation();
  const deleteMutation = useRiskDeleteEvalReviewMutation();

  const persistedVerdicts = useMemo(() => {
    const m = new Map<string, EvalVerdict>();
    for (const r of listQuery.data?.reviews ?? []) {
      m.set(r.chatId, r.verdict as EvalVerdict);
    }
    return m;
  }, [listQuery.data]);

  const verdicts = policyId ? persistedVerdicts : localVerdicts;

  const setVerdict = (chatId: string, verdict: EvalVerdict) => {
    const clearing = verdicts.get(chatId) === verdict; // clicking the active mark toggles it off
    if (!policyId) {
      setLocalVerdicts((prev) => {
        const next = new Map(prev);
        if (clearing) next.delete(chatId);
        else next.set(chatId, verdict);
        return next;
      });
      return;
    }
    const onSettled = () => void invalidateAllRiskListEvalReviews(queryClient);
    if (clearing) {
      deleteMutation.mutate({ request: { policyId, chatId } }, { onSettled });
    } else {
      saveMutation.mutate(
        {
          request: {
            saveRiskEvalReviewRequestBody: { policyId, chatId, verdict },
          },
        },
        { onSettled },
      );
    }
  };

  return { verdicts, setVerdict };
}

function EvalTuner({
  guardrail,
  onPromptChange,
  verdicts,
  setVerdict,
  reviewVerdictFilter,
  setReviewVerdictFilter,
}: {
  guardrail: Guardrail;
  onPromptChange: (v: string) => void;
  verdicts: Map<string, EvalVerdict>;
  setVerdict: (chatId: string, verdict: EvalVerdict) => void;
  reviewVerdictFilter: EvalVerdict | null;
  setReviewVerdictFilter: (verdict: EvalVerdict | null) => void;
}): JSX.Element {
  // Debounce guardrail edits before judging rows.
  const judgeGuardrail = useDebounced(guardrail, 600);
  const guardrailKey = useMemo(() => guardrailEvalKey(guardrail), [guardrail]);
  const judgeGuardrailKey = useMemo(
    () => guardrailEvalKey(judgeGuardrail),
    [judgeGuardrail],
  );
  const accumulatedEvalIdsRef = useRef<{
    key: string;
    ids: Set<string>;
  }>({ key: judgeGuardrailKey, ids: new Set() });
  const poisonRetryAfterRef = useRef(new Map<string, number>());
  const [query, setQuery] = useState("");
  const deferredQuery = useDeferredValue(query);
  const activeQuery = deferredQuery.trim();
  const chatsQuery = useListChats(
    {
      search: activeQuery || undefined,
      limit: EVAL_SESSION_LIMIT,
      sortBy: SortBy.LastMessageTimestamp,
      sortOrder: SortOrder.Desc,
    },
    undefined,
    {
      placeholderData: keepPreviousData,
    },
  );
  const rawChats = chatsQuery.data?.chats;
  const chats = useMemo(() => rawChats ?? [], [rawChats]);
  const visibleChatIds = useMemo(() => chats.map((chat) => chat.id), [chats]);
  const reviewSetChatIds = useMemo(
    () => Array.from(verdicts.keys()).sort(),
    [verdicts],
  );
  const evalChatIds = useMemo(() => {
    const current = accumulatedEvalIdsRef.current;
    if (current.key !== judgeGuardrailKey) {
      current.key = judgeGuardrailKey;
      current.ids = new Set();
    }
    for (const chatId of visibleChatIds) current.ids.add(chatId);
    for (const chatId of reviewSetChatIds) current.ids.add(chatId);
    return Array.from(current.ids).sort();
  }, [judgeGuardrailKey, reviewSetChatIds, visibleChatIds]);
  const hasJudgeGuardrail = judgeGuardrail.prompt.trim().length > 0;
  const client = useGramContext();
  const queryClient = useQueryClient();
  const evalQueries = useQueries({
    queries: evalChatIds.map((chatId) => {
      const request = evalRequestBody(judgeGuardrail, chatId);
      return {
        queryKey: evalQueryKey(judgeGuardrail, chatId),
        queryFn: ({ signal }: { signal: AbortSignal }) =>
          unwrapAsync(
            riskEvalsEvaluate(client, request, undefined, { signal }),
          ),
        enabled: hasJudgeGuardrail && evalChatIds.length > 0,
        ...JUDGE_QUERY_OPTIONS,
      };
    }),
  });
  const evalByChatId = useMemo(() => {
    const m = new Map<string, PromptGuardrailEvalResult>();
    for (const [index, chatId] of evalChatIds.entries()) {
      const query = evalQueries[index];
      if (
        query?.status === "success" &&
        query.fetchStatus !== "fetching" &&
        query.data?.chatId === chatId &&
        !isFailOpenCleanEval(query.data)
      ) {
        m.set(chatId, query.data);
      }
    }
    return m;
  }, [evalChatIds, evalQueries]);
  useEffect(() => {
    const now = Date.now();
    for (const [index, chatId] of evalChatIds.entries()) {
      const query = evalQueries[index];
      const queryKey = evalQueryKey(judgeGuardrail, chatId);
      if (
        query?.status === "success" &&
        query.fetchStatus !== "fetching" &&
        query.data?.chatId === chatId &&
        isFailOpenCleanEval(query.data)
      ) {
        const retryKey = `${judgeGuardrailKey}:${chatId}`;
        const retryAfter = poisonRetryAfterRef.current.get(retryKey) ?? 0;
        if (now >= retryAfter) {
          poisonRetryAfterRef.current.set(retryKey, now + 5000);
          void queryClient.invalidateQueries({ queryKey, exact: true });
        }
      }
    }
  }, [
    evalChatIds,
    evalQueries,
    judgeGuardrail,
    judgeGuardrailKey,
    queryClient,
  ]);
  const judgingChatIds = useMemo(() => {
    if (!hasJudgeGuardrail) return new Set<string>();
    return new Set(evalChatIds.filter((chatId) => !evalByChatId.has(chatId)));
  }, [evalByChatId, evalChatIds, hasJudgeGuardrail]);
  const reviewSetJudgingCount = useMemo(
    () =>
      reviewSetChatIds.filter((chatId) => judgingChatIds.has(chatId)).length,
    [judgingChatIds, reviewSetChatIds],
  );
  const evalJudging = judgingChatIds.size > 0;
  useEffect(() => {
    if (!reviewVerdictFilter) return;
    for (const current of verdicts.values()) {
      if (current === reviewVerdictFilter) return;
    }
    setReviewVerdictFilter(null);
  }, [reviewVerdictFilter, setReviewVerdictFilter, verdicts]);

  return (
    <div className="grid gap-6 @3xl:grid-cols-2">
      <Stack gap={4}>
        <GuardrailCard
          prompt={guardrail.prompt}
          onPromptChange={onPromptChange}
          rows={10}
        />
        <ReviewScorecard
          verdicts={verdicts}
          evalsByChatId={evalByChatId}
          judgingCount={reviewSetJudgingCount}
          activeVerdict={reviewVerdictFilter}
          onVerdictSelect={(next) =>
            setReviewVerdictFilter(reviewVerdictFilter === next ? null : next)
          }
        />
      </Stack>
      <SessionReview
        guardrail={judgeGuardrail}
        debouncePending={guardrailKey !== judgeGuardrailKey}
        evalJudging={evalJudging}
        evalByChatId={evalByChatId}
        judgingChatIds={judgingChatIds}
        query={query}
        setQuery={setQuery}
        activeQuery={activeQuery}
        chats={chats}
        chatsLoading={chatsQuery.isLoading}
        verdicts={verdicts}
        reviewVerdictFilter={reviewVerdictFilter}
        onClearReviewVerdictFilter={() => setReviewVerdictFilter(null)}
        onVerdict={setVerdict}
      />
    </div>
  );
}

function PromptReview({
  prompt,
  temperature,
  failOpen,
  mcpScope,
  customizedScopeCount,
  action,
  score,
  audienceType,
  audiencePrincipalCount,
  verdicts,
  activeVerdict,
  onVerdictSelect,
}: {
  prompt: string;
  temperature: number;
  failOpen: boolean;
  mcpScope: PolicyMCPScopeValue;
  customizedScopeCount: number;
  action: PolicyAction;
  score: number;
  audienceType: "everyone" | "targeted";
  audiencePrincipalCount: number;
  verdicts: Map<string, EvalVerdict>;
  activeVerdict: EvalVerdict | null;
  onVerdictSelect: (verdict: EvalVerdict) => void;
}): JSX.Element {
  const scopeText = scopeSummaryText(mcpScope, customizedScopeCount);

  return (
    <Stack gap={4}>
      <Card>
        <SectionHeader description="Confirm the guardrail, scope, and response before creating the policy." />
        <Stack gap={4}>
          <SummaryRow label="Guardrail">
            <Text small className="max-w-xl text-right">
              {prompt.trim() || "No guardrail prompt configured"}
            </Text>
          </SummaryRow>
          <SummaryRow label="Judge">
            <Text small className="text-right">
              temperature {temperature.toFixed(1)} ·{" "}
              {failOpen ? "fail open" : "fail closed"}
            </Text>
          </SummaryRow>
          <SummaryRow label="Scope">
            <Text small className="text-right">
              {scopeText}
            </Text>
          </SummaryRow>
          <SummaryRow label="Action">
            <Badge variant={action === "flag" ? "neutral" : "warning"}>
              {POLICY_ACTION_LABEL[action]}
            </Badge>
          </SummaryRow>
          <SummaryRow label="Severity">
            <SeverityBadge score={score} />
          </SummaryRow>
          <SummaryRow label="Audience">
            <Text small>
              {audienceType === "targeted"
                ? `${audiencePrincipalCount} targeted principal${
                    audiencePrincipalCount === 1 ? "" : "s"
                  }`
                : "Everyone"}
            </Text>
          </SummaryRow>
        </Stack>
      </Card>
      <ReviewScorecard
        verdicts={verdicts}
        activeVerdict={activeVerdict}
        onVerdictSelect={onVerdictSelect}
      />
    </Stack>
  );
}

function ReviewScorecard({
  verdicts,
  evalsByChatId = null,
  judgingCount = 0,
  activeVerdict,
  onVerdictSelect,
}: {
  verdicts: Map<string, EvalVerdict>;
  evalsByChatId?: Map<string, PromptGuardrailEvalResult> | null;
  judgingCount?: number;
  activeVerdict?: EvalVerdict | null;
  onVerdictSelect?: (verdict: EvalVerdict) => void;
}): JSX.Element {
  const reviewed = verdicts.size;
  const judged = evalsByChatId?.size ?? reviewed;
  let correct = 0;
  let falsePositive = 0;
  let missed = 0;
  if (evalsByChatId) {
    for (const [chatId, verdict] of verdicts) {
      const result = evalsByChatId.get(chatId);
      if (!result) continue;
      const agreement = agreementForVerdict(result.flagged, verdict);
      if (agreement === "agree") correct += 1;
      else if (result.flagged && verdict === "false_positive") {
        falsePositive += 1;
      } else if (!result.flagged && verdict === "missed") {
        missed += 1;
      }
    }
  } else {
    for (const v of verdicts.values()) {
      if (v === "correct") correct += 1;
      else if (v === "false_positive") falsePositive += 1;
      else if (v === "missed") missed += 1;
    }
  }

  return (
    <Card>
      <SectionHeader description="Your review set: how well the guardrail matches your judgment on the sessions you've checked. Edits replay automatically after a short pause." />
      {reviewed === 0 ? (
        <Text small muted>
          Review a few sessions on the right to build a scorecard. Aim for a
          couple of clear matches and a clean one or two.
        </Text>
      ) : (
        <Stack gap={4}>
          <div>
            <Text className="text-2xl font-semibold">
              {correct}/{judged}
            </Text>
            <Text small muted>
              {judgingCount > 0
                ? `${judgingCount} judging, ${reviewed} in review set`
                : `match your judgment across ${reviewed} reviewed ${
                    reviewed === 1 ? "session" : "sessions"
                  }`}
            </Text>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <ScoreStat
              label="Correct"
              value={correct}
              verdict="correct"
              active={activeVerdict === "correct"}
              onSelect={onVerdictSelect}
            />
            <ScoreStat
              label="False positives"
              value={falsePositive}
              verdict="false_positive"
              hint="tighten"
              warn={falsePositive > 0}
              active={activeVerdict === "false_positive"}
              onSelect={onVerdictSelect}
            />
            <ScoreStat
              label="Missed"
              value={missed}
              verdict="missed"
              hint="broaden"
              warn={missed > 0}
              active={activeVerdict === "missed"}
              onSelect={onVerdictSelect}
            />
          </div>
        </Stack>
      )}
    </Card>
  );
}

function ScoreStat({
  label,
  value,
  verdict,
  hint,
  warn,
  active,
  onSelect,
}: {
  label: string;
  value: number;
  verdict: EvalVerdict;
  hint?: string;
  warn?: boolean;
  active?: boolean;
  onSelect?: (verdict: EvalVerdict) => void;
}): JSX.Element {
  const clickable = value > 0 && onSelect;
  const content = (
    <>
      <Text className={cn("text-xl font-semibold", warn && "text-warning")}>
        {value}
      </Text>
      <Text small muted>
        {label}
      </Text>
      {hint && value > 0 ? (
        <Text small muted className="mt-0.5 italic">
          {hint}
        </Text>
      ) : null}
    </>
  );

  if (clickable) {
    return (
      <button
        type="button"
        onClick={() => onSelect(verdict)}
        aria-pressed={active}
        className={cn(
          "border p-3 text-left transition-colors",
          active
            ? "border-foreground/40 bg-muted/70"
            : "hover:bg-muted/40 hover:border-foreground/30",
        )}
      >
        {content}
      </button>
    );
  }

  return <div className="border p-3">{content}</div>;
}

function verdictForAgreement(
  flagged: boolean,
  agreement: JudgeAgreement,
): EvalVerdict {
  if (agreement === "agree") return "correct";
  return flagged ? "false_positive" : "missed";
}

function agreementForVerdict(
  flagged: boolean,
  verdict: EvalVerdict,
): JudgeAgreement | null {
  if (verdict === "correct") return "agree";
  if (flagged && verdict === "false_positive") return "disagree";
  if (!flagged && verdict === "missed") return "disagree";
  return null;
}

function evalVerdictLabel(verdict: EvalVerdict): string {
  switch (verdict) {
    case "correct":
      return "Correct";
    case "false_positive":
      return "False positive";
    case "missed":
      return "Missed";
  }
}

function judgeSessionLabel(flagged: boolean | undefined): string {
  if (flagged === undefined) return "Judge result pending";
  return flagged
    ? "Judge flagged this session"
    : "Judge found this session clean";
}

function agreementButtonClass(
  agreement: JudgeAgreement,
  selected: boolean,
): string | undefined {
  if (!selected) return undefined;
  return agreement === "agree"
    ? "!border-success !bg-success !text-success-foreground hover:!bg-success/90"
    : "!border-destructive/40 !bg-destructive/15 !text-destructive hover:!bg-destructive/20";
}

function rowHiddenByFilter(
  filter: EvalMatchFilter,
  flagged: boolean | undefined,
): boolean {
  if (filter === "all") return false;
  if (flagged === undefined) return true;
  return filter === "flagged" ? !flagged : flagged;
}

function sessionUserLabel(chat: {
  externalUserId?: string | undefined;
  userId?: string | undefined;
}): string {
  return chat.externalUserId || chat.userId || "Unknown user";
}

function highlightQuery(text: string, query: string): ReactNode {
  if (query.length === 0) return text;

  const matchIndex = text.toLowerCase().indexOf(query.toLowerCase());
  if (matchIndex === -1) return text;

  return (
    <>
      {text.slice(0, matchIndex)}
      <mark className="bg-yellow-200 px-0.5 text-foreground dark:bg-yellow-700/60">
        {text.slice(matchIndex, matchIndex + query.length)}
      </mark>
      {text.slice(matchIndex + query.length)}
    </>
  );
}

function reviewedChatIdsForVerdict(
  verdicts: Map<string, EvalVerdict>,
  verdict: EvalVerdict | null,
): string[] {
  if (!verdict) return [];
  const ids: string[] = [];
  for (const [chatId, current] of verdicts) {
    if (current === verdict) ids.push(chatId);
  }
  return ids;
}

function ReviewedSessionRows({
  chatIds,
  verdict,
  evalsByChatId,
  judgingChatIds,
  activeChatId,
  onClear,
  onSelect,
}: {
  chatIds: string[];
  verdict: EvalVerdict;
  evalsByChatId: Map<string, PromptGuardrailEvalResult>;
  judgingChatIds: Set<string>;
  activeChatId: string | null;
  onClear: () => void;
  onSelect: (chatId: string) => void;
}): JSX.Element {
  return (
    <div>
      <div className="bg-muted/30 flex items-center justify-between gap-3 border-b px-3 py-2">
        <div className="min-w-0">
          <Text small className="font-medium">
            {evalVerdictLabel(verdict)}
          </Text>
          <Text small muted>
            {chatIds.length} reviewed{" "}
            {chatIds.length === 1 ? "session" : "sessions"}
          </Text>
        </div>
        <Button variant="secondary" size="sm" onClick={onClear}>
          <Button.Text>Clear</Button.Text>
        </Button>
      </div>
      {chatIds.length === 0 ? (
        <Text small muted className="block px-3 py-6 text-center">
          No reviewed sessions for this verdict.
        </Text>
      ) : (
        chatIds.map((chatId, i) => (
          <ReviewedSessionRow
            key={chatId}
            chatId={chatId}
            verdict={verdict}
            evalResult={evalsByChatId.get(chatId)}
            judging={judgingChatIds.has(chatId)}
            active={chatId === activeChatId}
            first={i === 0}
            onSelect={() => onSelect(chatId)}
          />
        ))
      )}
    </div>
  );
}

function ReviewedSessionRow({
  chatId,
  verdict,
  evalResult,
  judging,
  active,
  first,
  onSelect,
}: {
  chatId: string;
  verdict: EvalVerdict;
  evalResult: PromptGuardrailEvalResult | undefined;
  judging: boolean;
  active: boolean;
  first: boolean;
  onSelect: () => void;
}): JSX.Element {
  const chatQuery = useLoadChat(
    { id: chatId, limit: 1, fromStart: true },
    undefined,
    {
      staleTime: 5 * 60 * 1000,
      throwOnError: false,
    },
  );
  const chat = chatQuery.data;
  const title = chat?.title || "Untitled session";
  const userLabel = chat ? sessionUserLabel(chat) : "Resolving session…";

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors",
        !first && "border-t",
        active ? "bg-muted/60" : "hover:bg-muted/30",
      )}
    >
      <div className="min-w-0 flex-1">
        <Text small className="truncate font-medium">
          {title}
        </Text>
        <Text small muted className="flex min-w-0 items-center gap-1 truncate">
          <span className="truncate">{userLabel}</span>
          {chat ? (
            <>
              <span>·</span>
              <span>{formatRelative(chat.lastMessageTimestamp)}</span>
            </>
          ) : null}
          {chatQuery.isError ? (
            <>
              <span>·</span>
              <span>metadata unavailable</span>
            </>
          ) : null}
        </Text>
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        <SessionMatchBadge
          enabled
          judging={judging}
          flagged={evalResult?.flagged}
        />
        <Badge variant={verdict === "missed" ? "warning" : "neutral"}>
          {evalVerdictLabel(verdict)}
        </Badge>
      </div>
    </button>
  );
}

function SessionReview({
  guardrail,
  debouncePending,
  evalJudging,
  evalByChatId,
  judgingChatIds,
  query,
  setQuery,
  activeQuery,
  chats,
  chatsLoading,
  verdicts,
  reviewVerdictFilter,
  onClearReviewVerdictFilter,
  onVerdict,
}: {
  guardrail: Guardrail;
  debouncePending: boolean;
  evalJudging: boolean;
  evalByChatId: Map<string, PromptGuardrailEvalResult>;
  judgingChatIds: Set<string>;
  query: string;
  setQuery: (query: string) => void;
  activeQuery: string;
  chats: ChatOverview[];
  chatsLoading: boolean;
  verdicts: Map<string, EvalVerdict>;
  reviewVerdictFilter: EvalVerdict | null;
  onClearReviewVerdictFilter: () => void;
  onVerdict: (chatId: string, verdict: EvalVerdict) => void;
}): JSX.Element {
  const [filter, setFilter] = useState<EvalMatchFilter>("all");
  const [selectedIdState, setSelectedIdState] = useState<string | null>(null);
  const reviewedChatIds = useMemo(
    () => reviewedChatIdsForVerdict(verdicts, reviewVerdictFilter),
    [verdicts, reviewVerdictFilter],
  );
  const hasGuardrail = guardrail.prompt.trim().length > 0;
  const visibleChats = useMemo(
    () =>
      chats.filter(
        (chat) =>
          !rowHiddenByFilter(filter, evalByChatId.get(chat.id)?.flagged),
      ),
    [chats, evalByChatId, filter],
  );

  // Do not auto-open a row while search/filter results change.
  const selectedId =
    selectedIdState &&
    (chats.some((c) => c.id === selectedIdState) ||
      reviewedChatIds.includes(selectedIdState))
      ? selectedIdState
      : null;
  const selectedChat = chats.find((c) => c.id === selectedId) ?? null;
  const reevaluating = hasGuardrail && (debouncePending || evalJudging);

  return (
    <Card className="flex flex-col">
      <SectionHeader description="Search by title or user, review how this guardrail judges the transcript, then mark the verdict." />
      <div className="flex min-h-0 flex-1 flex-col gap-4">
        {/* Search */}
        <Stack gap={2}>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              value={query}
              onChange={setQuery}
              placeholder="Search by title or user"
              className="min-w-48 flex-1"
            />
            <ReevaluatingIndicator show={reevaluating} />
          </div>
          <div className="border-border inline-flex self-start border p-0.5">
            {(
              [
                { key: "all", label: "All" },
                { key: "flagged", label: "Flagged" },
                { key: "clean", label: "Clean" },
              ] as const
            ).map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setFilter(opt.key)}
                className={cn(
                  "px-3 py-1 text-xs font-medium transition-colors",
                  filter === opt.key
                    ? "bg-foreground text-background"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </Stack>

        {/* Results list */}
        <div className="min-h-0 flex-1 overflow-auto border">
          {reviewVerdictFilter ? (
            <ReviewedSessionRows
              chatIds={reviewedChatIds}
              verdict={reviewVerdictFilter}
              evalsByChatId={evalByChatId}
              judgingChatIds={judgingChatIds}
              activeChatId={selectedId}
              onClear={onClearReviewVerdictFilter}
              onSelect={setSelectedIdState}
            />
          ) : chatsLoading ? (
            <Text small muted className="block px-3 py-6 text-center">
              Loading sessions…
            </Text>
          ) : chats.length === 0 ? (
            <Text small muted className="block px-3 py-6 text-center">
              No sessions match your search.
            </Text>
          ) : visibleChats.length === 0 ? (
            <Text small muted className="block px-3 py-6 text-center">
              No sessions match this judge filter.
            </Text>
          ) : (
            visibleChats.map((chat, i) => (
              <SessionRow
                key={chat.id}
                chat={chat}
                active={chat.id === selectedId}
                first={i === 0}
                evalEnabled={hasGuardrail}
                flagged={evalByChatId.get(chat.id)?.flagged}
                judging={judgingChatIds.has(chat.id)}
                reviewVerdict={verdicts.get(chat.id) ?? null}
                searchQuery={activeQuery}
                onSelect={() => setSelectedIdState(chat.id)}
              />
            ))
          )}
        </div>
      </div>
      <EvalSessionTranscriptSheet
        chatId={selectedId}
        chat={selectedChat}
        guardrail={guardrail}
        verdict={selectedId ? (verdicts.get(selectedId) ?? null) : null}
        reviewDisabled={debouncePending}
        onClose={() => setSelectedIdState(null)}
        onVerdict={(v) => {
          if (selectedId) onVerdict(selectedId, v);
        }}
      />
    </Card>
  );
}

function ReevaluatingIndicator({
  show,
}: {
  show: boolean;
}): JSX.Element | null {
  if (!show) return null;

  return (
    <div className="border-border bg-muted/30 text-muted-foreground flex h-9 items-center gap-1.5 border px-2.5">
      <Loader2 className="h-3.5 w-3.5 animate-spin" />
      <Text small muted>
        Re-evaluating…
      </Text>
    </div>
  );
}

function SessionRow({
  chat,
  active,
  first,
  evalEnabled,
  flagged,
  judging,
  reviewVerdict,
  searchQuery,
  onSelect,
}: {
  chat: ChatOverview;
  active: boolean;
  first: boolean;
  evalEnabled: boolean;
  flagged: boolean | undefined;
  judging: boolean;
  reviewVerdict: EvalVerdict | null;
  searchQuery: string;
  onSelect: () => void;
}): JSX.Element {
  const title = chat.title || "Untitled session";
  const userLabel = sessionUserLabel(chat);

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors",
        !first && "border-t",
        active ? "bg-muted/60" : "hover:bg-muted/30",
      )}
    >
      <div className="min-w-0 flex-1">
        <Text small className="truncate font-medium">
          {highlightQuery(title, searchQuery)}
        </Text>
        <Text small muted className="flex min-w-0 items-center gap-1 truncate">
          <span className="truncate">
            {highlightQuery(userLabel, searchQuery)}
          </span>
          <span>·</span>
          <span>{formatRelative(chat.lastMessageTimestamp)}</span>
        </Text>
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        <SessionMatchBadge
          enabled={evalEnabled}
          judging={judging || flagged === undefined}
          flagged={flagged}
        />
        {reviewVerdict ? (
          <Badge variant="neutral">{evalVerdictLabel(reviewVerdict)}</Badge>
        ) : null}
      </div>
    </button>
  );
}

function SessionMatchBadge({
  enabled,
  judging,
  flagged,
}: {
  enabled: boolean;
  judging: boolean;
  flagged: boolean | undefined;
}): JSX.Element | null {
  if (!enabled) return null;
  if (flagged === undefined) {
    return (
      <Badge variant="neutral">{judging ? "Judging…" : "Not judged"}</Badge>
    );
  }
  return (
    <Badge variant={flagged ? "destructive" : "neutral"}>
      {flagged ? "Flagged" : "Clean"}
    </Badge>
  );
}

function EvalSessionTranscriptSheet({
  chatId,
  chat,
  guardrail,
  verdict,
  reviewDisabled,
  onClose,
  onVerdict,
}: {
  chatId: string | null;
  chat: ChatOverview | null;
  guardrail: Guardrail;
  verdict: EvalVerdict | null;
  reviewDisabled: boolean;
  onClose: () => void;
  onVerdict: (v: EvalVerdict) => void;
}): JSX.Element {
  return (
    <Sheet
      open={Boolean(chatId)}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SheetContent
        className="flex h-full w-[min(760px,calc(100vw-2rem))] flex-col gap-0 p-0 sm:max-w-[760px]"
        showCloseButton={false}
      >
        {chatId && (
          <SessionTranscript
            chatId={chatId}
            chat={chat}
            guardrail={guardrail}
            verdict={verdict}
            reviewDisabled={reviewDisabled}
            onClose={onClose}
            onVerdict={onVerdict}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

function EvalJudgeVerdictBlock({
  verdict,
}: {
  verdict: PromptGuardrailMessageVerdict;
}): JSX.Element {
  return (
    <div className="border-warning bg-warning/10 border-l-[3px] px-3 py-2.5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs font-semibold">
          <TriangleAlert className="text-warning size-4 shrink-0" />
          <span>LLM Judge</span>
          <Badge variant="warning" background>
            Flagged
          </Badge>
        </div>
        <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
          {formatEvalConfidence(verdict.confidence)} ·{" "}
          {formatEvalLatency(verdict.latencyMs)}
        </span>
      </div>
      <div className="text-foreground mt-1 text-xs leading-relaxed">
        {verdict.rationale || "Flagged by the guardrail"}
      </div>
    </div>
  );
}

function JudgeSessionBanner({
  evalResult,
  fetching,
}: {
  evalResult: PromptGuardrailEvalResult | undefined;
  fetching: boolean;
}): JSX.Element {
  if (fetching && !evalResult) {
    return (
      <div className="border-border bg-muted/30 flex items-start gap-3 border-b px-4 py-3">
        <Loader2 className="text-muted-foreground mt-0.5 size-4 shrink-0 animate-spin" />
        <div className="min-w-0">
          <Text small className="font-medium">
            Judge is evaluating this session
          </Text>
          <Text small muted>
            Verdicts will appear against matching messages when the run
            finishes.
          </Text>
        </div>
      </div>
    );
  }

  if (!evalResult) {
    return (
      <div className="border-border bg-muted/20 flex items-start gap-3 border-b px-4 py-3">
        <Shield className="text-muted-foreground mt-0.5 size-4 shrink-0" />
        <div className="min-w-0">
          <Text small className="font-medium">
            Opened for judge evaluation
          </Text>
          <Text small muted>
            The session has not returned a judge result yet.
          </Text>
        </div>
      </div>
    );
  }

  const matchedCount = evalResult.verdicts.filter((v) => v.matched).length;
  const judgedLabel = `${evalResult.judgedCount} judged ${
    evalResult.judgedCount === 1 ? "message" : "messages"
  }`;
  const detail = `${matchedCount} ${
    matchedCount === 1 ? "message" : "messages"
  } matched`;
  let messageLimitNote = "";
  if (evalResult.messageLimitHit) {
    messageLimitNote = ` Only the first ${evalResult.judgedCount} of ${evalResult.inScopeMessageCount} in-scope messages were judged.`;
  }

  if (evalResult.flagged) {
    return (
      <div className="border-warning/40 bg-warning/15 flex items-start gap-3 border-b px-4 py-3">
        <TriangleAlert className="text-warning mt-0.5 size-4 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Text small className="font-semibold">
              Judge flagged this session
            </Text>
            <Badge variant="warning" background>
              Flagged
            </Badge>
          </div>
          <Text small muted>
            {detail} across {judgedLabel}. Matching messages are highlighted
            below.{messageLimitNote}
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div className="border-success/35 bg-success/10 flex items-start gap-3 border-b px-4 py-3">
      <Check className="text-success mt-0.5 size-4 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <Text small className="font-semibold">
            Judge found this session clean
          </Text>
          <Badge variant="neutral">Clean</Badge>
        </div>
        <Text small muted>
          No messages matched across {judgedLabel}.{messageLimitNote}
        </Text>
      </div>
    </div>
  );
}

function SessionTranscript({
  chatId,
  chat,
  guardrail,
  verdict,
  reviewDisabled,
  onClose,
  onVerdict,
}: {
  chatId: string;
  chat: ChatOverview | null;
  guardrail: Guardrail;
  verdict: EvalVerdict | null;
  reviewDisabled: boolean;
  onClose: () => void;
  onVerdict: (v: EvalVerdict) => void;
}): JSX.Element {
  const transcript = useChatTranscript(chatId, true);
  const headerChat = chat ?? transcript.chat ?? null;
  const judge = usePromptGuardrailEval(
    guardrail,
    chatId,
    guardrail.prompt.trim().length > 0,
  );
  const evalResult = judge.data;
  const judgeFetching = judge.isFetching;

  const verdictByMessage = useMemo(() => {
    const m = new Map<string, PromptGuardrailMessageVerdict>();
    for (const v of evalResult?.verdicts ?? []) m.set(v.messageId, v);
    return m;
  }, [evalResult]);

  const rows = useMemo(
    () => buildTranscript(transcript.messages),
    [transcript.messages],
  );
  const displayItems = useMemo(
    () =>
      buildDisplayItems({
        rows,
        hasMoreBefore: transcript.hasMoreBefore,
        hasMoreAfter: transcript.hasMoreAfter,
      }),
    [rows, transcript.hasMoreBefore, transcript.hasMoreAfter],
  );
  const pagination = useMemo<TranscriptPagination>(
    () => ({
      hasMoreBefore: transcript.hasMoreBefore,
      hasMoreAfter: transcript.hasMoreAfter,
      onLoadOlder: transcript.fetchOlder,
      onLoadNewer: transcript.fetchNewer,
      // The from-start transcript's only missing range is the tail, so the
      // break button drains the rest of the conversation.
      onLoadAllOlder: transcript.fetchOlder,
      onLoadAllNewer: transcript.loadRest,
      isFetchingOlder: transcript.isFetchingOlder,
      isFetchingNewer: transcript.isFetchingNewer || transcript.isLoadingRest,
      initialScrollIndex: null,
      scrollToFinding: false,
    }),
    [
      transcript.hasMoreBefore,
      transcript.hasMoreAfter,
      transcript.fetchOlder,
      transcript.fetchNewer,
      transcript.loadRest,
      transcript.isFetchingOlder,
      transcript.isFetchingNewer,
      transcript.isLoadingRest,
    ],
  );
  const rowCtx = useMemo<RowContext>(
    () => ({
      dimNonRisk: false,
      userLabel: headerChat ? sessionUserLabel(headerChat) : undefined,
      rowDecoration: (messageIds) => {
        const matched = messageIds
          .map((id) => verdictByMessage.get(id))
          .find((v) => v?.matched);
        if (!matched) return null;
        return {
          tone: "warning",
          footer: <EvalJudgeVerdictBlock verdict={matched} />,
        };
      },
    }),
    [headerChat, verdictByMessage],
  );
  const judgeFlagged = evalResult?.flagged;
  const judgeSettled = judgeFlagged !== undefined && !judgeFetching;
  const canReview = judgeSettled && !reviewDisabled;

  return (
    <div className="bg-background flex h-full min-h-0 flex-col">
      <SheetHeader className="border-b px-4 py-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 flex-col gap-1.5">
            <SheetTitle className="truncate text-base">
              {headerChat?.title || "Untitled session"}
            </SheetTitle>
            <SheetDescription asChild>
              <div className="flex flex-col gap-2">
                <div className="text-muted-foreground flex min-w-0 items-center gap-1.5 text-sm">
                  <span className="truncate">
                    {headerChat
                      ? sessionUserLabel(headerChat)
                      : "Resolving session…"}
                  </span>
                  {headerChat ? (
                    <>
                      <span>·</span>
                      <span>
                        {formatRelative(headerChat.lastMessageTimestamp)}
                      </span>
                    </>
                  ) : null}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {headerChat ? (
                    <Badge variant="neutral">
                      {headerChat.numMessages} messages
                    </Badge>
                  ) : null}
                  {headerChat?.source ? (
                    <Badge variant="neutral">{headerChat.source}</Badge>
                  ) : null}
                  {evalResult ? (
                    <Badge variant="neutral">
                      {formatEvalCostLatency(
                        evalResult.totalCostUsd,
                        evalResult.totalLatencyMs,
                      )}
                    </Badge>
                  ) : null}
                  {judgeFetching ? (
                    <Badge variant="neutral">Judging…</Badge>
                  ) : null}
                </div>
              </div>
            </SheetDescription>
          </div>
          <button
            onClick={onClose}
            className="hover:bg-muted p-1 transition-colors"
            aria-label="Close panel"
          >
            <X className="size-5" />
          </button>
        </div>
      </SheetHeader>

      <JudgeSessionBanner evalResult={evalResult} fetching={judgeFetching} />

      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {transcript.isLoading ? (
            <Text small muted className="block p-6 text-center">
              Loading transcript…
            </Text>
          ) : transcript.isError ? (
            <Text small muted className="block p-6 text-center">
              Failed to load transcript.
            </Text>
          ) : transcript.messages.length === 0 ? (
            <Text small muted className="block p-6 text-center">
              This session has no messages.
            </Text>
          ) : (
            <ChatTranscript
              items={displayItems}
              ctx={rowCtx}
              pagination={pagination}
              emptyMessage="This session has no messages."
            />
          )}
        </div>
      </div>

      <SheetFooter className="border-t px-4 py-3">
        <ReviewAgreementControl
          flagged={judgeFlagged}
          verdict={verdict}
          disabled={!canReview}
          onVerdict={onVerdict}
        />
      </SheetFooter>
    </div>
  );
}

function ReviewAgreementControl({
  flagged,
  verdict,
  disabled,
  onVerdict,
}: {
  flagged: boolean | undefined;
  verdict: EvalVerdict | null;
  disabled: boolean;
  onVerdict: (v: EvalVerdict) => void;
}): JSX.Element {
  const currentAgreement =
    flagged === undefined || verdict === null
      ? null
      : agreementForVerdict(flagged, verdict);
  const staleReview =
    verdict !== null && flagged !== undefined && !currentAgreement;
  const judgeLabel = judgeSessionLabel(flagged);
  const reviewPrompt =
    flagged === undefined
      ? "Review available after judging completes."
      : "Was the judge right?";

  const handleAgreement = (agreement: JudgeAgreement) => {
    if (flagged === undefined) return;
    onVerdict(verdictForAgreement(flagged, agreement));
  };

  return (
    <Stack gap={3} align="center">
      <Stack gap={1} align="center" className="text-center">
        <Text small muted className="font-medium">
          {judgeLabel}
        </Text>
        <Text small muted>
          {reviewPrompt}
        </Text>
        {staleReview ? (
          <Text small muted className="italic">
            Reviewed against an earlier prompt: {evalVerdictLabel(verdict)}
          </Text>
        ) : null}
      </Stack>
      <Stack
        direction="horizontal"
        gap={2}
        align="center"
        justify="center"
        className="w-full"
      >
        {(
          [
            { key: "agree", label: "Right", icon: ThumbsUp },
            { key: "disagree", label: "Wrong", icon: ThumbsDown },
          ] as const
        ).map((opt) => {
          const selected = currentAgreement === opt.key;
          const IconComponent = opt.icon;

          return (
            <Button
              key={opt.key}
              variant="secondary"
              size="sm"
              disabled={disabled}
              className={cn(
                "min-w-24 justify-center",
                agreementButtonClass(opt.key, selected),
              )}
              onClick={() => handleAgreement(opt.key)}
            >
              <Button.LeftIcon>
                <IconComponent className="h-4 w-4" />
              </Button.LeftIcon>
              <Button.Text>{opt.label}</Button.Text>
            </Button>
          );
        })}
      </Stack>
    </Stack>
  );
}

// ── Standard policy editor (stepped: Detect → Scope → Action → Review) ────────
// Reuses PolicyCenter's detector/scope/action/audience building blocks and its
// payload mapping. `policy === null` means create mode.

export function StandardPolicyEditor({
  policy,
  prefill,
  draft = null,
}: {
  policy: RiskPolicy | null;
  /** Starting values for a new policy; ignored when editing one. */
  prefill?: PolicyNewPrefill;
  draft?: PolicyDraft | null;
}): JSX.Element {
  const routes = useRoutes();
  const gramProject = useProjectSlugForRequests();
  const project = useProject();
  const queryClient = useQueryClient();
  const { customRules } = useDetectionRulesStore();
  // Under the LLM analyzer flag the personal-data categories collapse into a
  // single category-level `pii` detector and the Presidio-only controls
  // (entity selection, sensitivity) leave the form. See `DetectorMode`.
  const mode = useDetectorMode();
  const categoriesQuery = useRiskCategories();
  const [initializedInventoryForPolicy, setInitializedInventoryForPolicy] =
    useState<string | null>(null);

  const [step, setStep] = useStepParam(STANDARD_STEPS);

  // Original values (edit mode) used for dirty tracking.
  const orig = useMemo(() => {
    if (!policy) return null;
    const cats = policyToCategories(
      policy.sources,
      policy.presidioEntities,
      mode,
    );
    if ((policy.customRuleIds ?? []).length > 0) cats.add("custom");
    return {
      name: policy.name,
      action: (policy.action as PolicyAction) ?? "flag",
      userMessage: policy.userMessage ?? "",
      disabledRules: new Set(policy.disabledRules ?? []),
      scopeOverrides: scopeOverridesFromPolicy(policy.detectionScopes),
      mcpScope: policy.mcpScope ?? null,
      customRuleIds: new Set(policy.customRuleIds ?? []),
      categories: cats,
      approvedDomains: (policy.approvedEmailDomains ?? []).join(", "),
      audienceType:
        policy.audienceType === "targeted"
          ? ("targeted" as const)
          : ("everyone" as const),
      audiencePrincipalUrns:
        policy.audienceType === "targeted"
          ? new Set(policy.audiencePrincipalUrns ?? [])
          : new Set<string>(),
      score: policy.score ?? 5,
      presidioThreshold:
        policy.presidioScoreThreshold ?? DEFAULT_PRESIDIO_THRESHOLD,
    };
  }, [policy, mode]);

  // ── Local form state, seeded from the policy (edit), a draft from the
  // intent screen, or prefill and defaults (create). ──
  const seed = policy ? undefined : prefill;
  const [name, setName] = useState(
    policy?.name ?? draft?.name ?? seed?.name ?? "",
  );
  const [selectedCategories, setSelectedCategories] = useState<
    Set<RuleCategory>
  >(
    () =>
      new Set(
        orig?.categories ??
          (draft
            ? policyToCategories(draft.sources, draft.presidioEntities, mode)
            : seed?.categories),
      ),
  );
  // Categories the draft turned on, tagged on the detect step. Recomputed
  // with `mode`, which resolves after mount.
  const draftCategories = useMemo(
    () =>
      !policy && draft?.origin
        ? policyToCategories(draft.sources, draft.presidioEntities, mode)
        : new Set<RuleCategory>(),
    [policy, draft, mode],
  );
  // The flag behind `mode` resolves asynchronously, so the seed above may
  // predate it; fold any legacy personal-data selection into `pii` once the
  // LLM analyzer applies, or the collapsed card would hide it and a save
  // would drop the presidio source.
  useEffect(() => {
    setSelectedCategories((prev) => normalizeCategoriesForMode(prev, mode));
  }, [mode]);
  const [disabledRules, setDisabledRules] = useState<Set<string>>(
    () => new Set(policy?.disabledRules ?? []),
  );
  const [scopeOverrides, setScopeOverrides] = useState<
    Map<string, ScopeOverride>
  >(() => scopeOverridesFromPolicy(policy?.detectionScopes));
  const [mcpScope, setMcpScope] = useState<PolicyMCPScopeValue>(() =>
    seed && seed.mcpServerIds.length > 0
      ? {
          mode: "mcp",
          allServers: false,
          toolAnnotations: [],
          servers: seed.mcpServerIds.map((mcpServerId) => ({ mcpServerId })),
        }
      : ((policy ? null : draftMCPScopeValue(draft)) ??
        initialPolicyMCPScopeValue(policy)),
  );
  const [draftScopeNames, setDraftScopeNames] = useState<string[]>(
    policy ? [] : (draft?.mcpScope?.matchedNames ?? []),
  );
  const [selectedCustomRuleIds, setSelectedCustomRuleIds] = useState<
    Set<string>
  >(() => new Set(policy?.customRuleIds ?? []));
  const initialAction =
    (policy?.action as PolicyAction) ?? draft?.action ?? seed?.action ?? "flag";
  const [action, setAction] = useState<PolicyAction>(
    mcpScope.mode === "mcp"
      ? mcpCompatibleAction(initialAction)
      : initialAction,
  );
  // Under block_all the URL set holds allowed servers; under allow_all it
  // holds blocked servers. The disposition is immutable after create, so the
  // meaning never flips for an existing policy.
  const [selectedShadowMCPURLs, setSelectedShadowMCPURLs] = useState<
    Set<string>
  >(() => new Set());
  const [originalShadowMCPURLs, setOriginalShadowMCPURLs] =
    useState<Set<string> | null>(null);
  // Standing decisions the pending save would contradict; non-null opens the
  // supersede confirmation.
  const [supersedeConflicts, setSupersedeConflicts] = useState<
    ShadowMCPDecisionConflict[] | null
  >(null);
  const [shadowMCPDisposition, setShadowMCPDisposition] =
    useState<ShadowMCPDisposition>(
      () =>
        (policy?.shadowMcpDisposition as ShadowMCPDisposition) ?? "block_all",
    );
  const [userMessage, setUserMessage] = useState(
    policy?.userMessage ?? draft?.userMessage ?? "",
  );
  const [audienceType, setAudienceType] = useState<"everyone" | "targeted">(
    policy?.audienceType === "targeted" ? "targeted" : "everyone",
  );
  const [audiencePrincipalUrns, setAudiencePrincipalUrns] = useState<
    Set<string>
  >(() =>
    policy?.audienceType === "targeted"
      ? new Set(policy.audiencePrincipalUrns ?? [])
      : new Set<string>(),
  );
  // Approved email domains for the Non-Corporate Accounts category, held as
  // the raw comma-separated input. Only sent when the category is selected.
  const [approvedDomains, setApprovedDomains] = useState(() =>
    (policy?.approvedEmailDomains ?? []).join(", "),
  );
  const [customizeCategory, setCustomizeCategory] =
    useState<RuleCategory | null>(null);
  const [detectionExpanded, setDetectionExpanded] = useState(true);
  const [score, setScore] = useState(
    policy?.score ?? draft?.score ?? seed?.score ?? 5,
  );
  const [presidioThreshold, setPresidioThreshold] = useState<number>(
    policy?.presidioScoreThreshold ?? DEFAULT_PRESIDIO_THRESHOLD,
  );

  // ── Derived state ──
  const targetIsShadowMCPBlock = isBlockingShadowMCPPolicy(
    true,
    [...selectedCategories],
    action,
  );
  const inventoryQuery = useShadowMCPPolicyInventory(
    project.id,
    targetIsShadowMCPBlock,
  );
  const policyID = policy?.id ?? null;
  const editorIdentity = policyID ?? "create";
  const originalHasShadowMCPBlockConfiguration = policy
    ? isShadowMCPBlockConfiguration(policy.sources, policy.action)
    : false;
  // An allow_all policy cannot stop detecting Shadow MCP or stop denying: its
  // blocked-server list would have nothing to enforce, and the server rejects
  // the save. Pin both controls so that shows up here, not in an error toast.
  const allowAllPostureLocked = shadowMCPAllowAllPostureLocked(policy);

  useEffect(() => {
    if (
      !targetIsShadowMCPBlock ||
      !inventoryQuery.data ||
      initializedInventoryForPolicy === editorIdentity
    ) {
      return;
    }

    let initialURLs: ReadonlySet<string> = new Set<string>();
    if (policyID && originalHasShadowMCPBlockConfiguration) {
      // Both dispositions derive their URL set from per-URL grants surfaced
      // on the inventory: bypass grants (allowed) for block_all policies,
      // block grants (blocked) for allow_all policies.
      initialURLs =
        policy?.shadowMcpDisposition === "allow_all"
          ? initialShadowMCPBlockedPolicyURLs(inventoryQuery.data, policyID)
          : initialShadowMCPPolicyURLs(inventoryQuery.data, policyID);
    }
    setSelectedShadowMCPURLs(new Set(initialURLs));
    setOriginalShadowMCPURLs(new Set(initialURLs));
    setInitializedInventoryForPolicy(editorIdentity);
  }, [
    editorIdentity,
    initializedInventoryForPolicy,
    inventoryQuery.data,
    originalHasShadowMCPBlockConfiguration,
    policy,
    policyID,
    targetIsShadowMCPBlock,
  ]);

  const flagOnlySelected = [...FLAG_ONLY_CATEGORIES].some((c) =>
    selectedCategories.has(c),
  );
  const personalDataActive = personalDataCategories(mode).some((c) =>
    selectedCategories.has(c),
  );
  // The Presidio confidence threshold only exists for the legacy engine; the
  // LLM analyzer's score is binary, so the control and the field disappear.
  const presidioActive = mode === "presidio" && personalDataActive;
  // The banner tells flagged orgs that the policy's stored Presidio settings
  // are not what runs: shown for any personal-data policy, stored or drafted.
  const llmAnalyzerActive =
    mode === "llm" &&
    (personalDataActive || (policy?.sources ?? []).includes("presidio"));
  const modeCategoryLevelDetectors = categoryLevelDetectors(mode);
  const hasEnabledDetector =
    selectedCustomRuleIds.size > 0 ||
    [...selectedCategories].some(
      (c) =>
        modeCategoryLevelDetectors.has(c) ||
        DETECTION_RULES[c]?.some((r) => !r.hidden && !disabledRules.has(r.id)),
    );
  const audienceMissing =
    audienceType === "targeted" && audiencePrincipalUrns.size === 0;
  const shadowMCPInventoryUnavailable =
    targetIsShadowMCPBlock &&
    (inventoryQuery.isPending ||
      inventoryQuery.isError ||
      inventoryQuery.data === undefined);
  const shadowMCPSelectionInitialized = shadowMCPSelectionIsInitialized(
    targetIsShadowMCPBlock,
    initializedInventoryForPolicy,
    editorIdentity,
  );
  const mcpScopePayload = policyMCPScopePayload(mcpScope);
  const mcpScopeMissingServers =
    mcpScope.mode === "mcp" &&
    !mcpScope.allServers &&
    mcpScope.servers.length === 0;
  const mcpScopeIncompatible =
    mcpScope.mode === "mcp" &&
    (selectedCategories.has("account_identity") ||
      selectedCategories.has("shadow_mcp"));
  const inspectRowsValid = inspectRowsAreValid(
    selectedCategories,
    scopeOverrides,
    categoriesQuery.data?.categories,
    mcpScope.mode === "mcp",
  );
  // A draft from a use case that needs approved domains can't be created
  // without them while its Non-Corporate Accounts detector is on.
  const approvedDomainsRequired =
    !policy &&
    draft?.requiresApprovedEmailDomains === true &&
    selectedCategories.has("account_identity");
  const parsedApprovedDomains = parseApprovedEmailDomains(approvedDomains);
  const invalidApprovedDomains = invalidEmailDomains(parsedApprovedDomains);
  const approvedDomainsMissing =
    approvedDomainsRequired &&
    (parsedApprovedDomains.length === 0 || invalidApprovedDomains.length > 0);

  const saveBlocked =
    !hasEnabledDetector ||
    approvedDomainsMissing ||
    audienceMissing ||
    shadowMCPInventoryUnavailable ||
    mcpScope.mode === "unset" ||
    mcpScopeMissingServers ||
    mcpScopeIncompatible ||
    !inspectRowsValid ||
    !shadowMCPSelectionInitialized;

  const shadowMCPSelectionDirty = shadowMCPSelectionIsDirty(
    targetIsShadowMCPBlock,
    selectedShadowMCPURLs,
    originalShadowMCPURLs,
  );

  const dirty =
    !!orig &&
    (name !== orig.name ||
      action !== orig.action ||
      userMessage !== orig.userMessage ||
      audienceType !== orig.audienceType ||
      !sameSet(disabledRules, orig.disabledRules) ||
      !sameScopeOverrides(scopeOverrides, orig.scopeOverrides) ||
      !sameMCPScope(mcpScopePayload, orig.mcpScope) ||
      !sameSet(selectedCustomRuleIds, orig.customRuleIds) ||
      !sameSet(selectedCategories, orig.categories) ||
      approvedDomains !== orig.approvedDomains ||
      score !== orig.score ||
      (presidioActive && presidioThreshold !== orig.presidioThreshold) ||
      !sameSet(audiencePrincipalUrns, orig.audiencePrincipalUrns) ||
      shadowMCPSelectionDirty);

  const updateMutation = useRiskPoliciesUpdateMutation({
    onSuccess: (_policy, variables) => {
      setSupersedeConflicts(null);
      const submittedURLs = shadowMCPSelectionBaselineForUpdate(
        variables.request.updateRiskPolicyRequestBody,
      );
      if (submittedURLs !== undefined) {
        setSelectedShadowMCPURLs(new Set(submittedURLs));
        setOriginalShadowMCPURLs(new Set(submittedURLs));
      }
      void invalidateAllRiskPoliciesGet(queryClient);
      void invalidateAllRiskListPolicies(queryClient);
      void invalidateAllShadowMCPInventory(queryClient);
      void invalidateShadowMCPPolicyInventory(queryClient, project.id);
      toast.success("Policy updated");
    },
  });
  const createMutation = useRiskCreatePolicyMutation({
    onSuccess: () => {
      clearPolicyStartSession(gramProject);
      void invalidateAllRiskListPolicies(queryClient);
      void invalidateAllShadowMCPInventory(queryClient);
      void invalidateShadowMCPPolicyInventory(queryClient, project.id);
      routes.policyCenter.goTo();
    },
  });
  const saving = updateMutation.isPending || createMutation.isPending;
  const updateMCPScope = (next: PolicyMCPScopeValue) => {
    setMcpScope(next);
    if (next.mode === "mcp") {
      setAction((current) => mcpCompatibleAction(current));
    }
  };

  // Toggle a whole built-in detector category (clears its per-rule disables).
  // Flag-only categories force the policy action to flag.
  const toggleCategory = (cat: RuleCategory, checked: boolean) => {
    if (
      checked &&
      builtInRuleDisabledReason(cat, selectedCategories) !== undefined
    ) {
      return;
    }

    // A category-level detector has no rule list to reset; under the LLM
    // analyzer that includes `pii`, whose stored Presidio overrides must
    // survive a flip so a flag-off restores them.
    const rules = modeCategoryLevelDetectors.has(cat)
      ? []
      : DETECTION_RULES[cat].filter((r) => !r.hidden);
    const nextCats = new Set(selectedCategories);
    const nextDisabled = new Set(disabledRules);
    if (checked) nextCats.add(cat);
    else nextCats.delete(cat);
    for (const rule of rules) nextDisabled.delete(rule.id);
    setSelectedCategories(nextCats);
    setDisabledRules(nextDisabled);
    if (checked && FLAG_ONLY_CATEGORIES.has(cat) && action !== "flag") {
      setAction("flag");
    }
  };
  const toggleDetector = (ruleId: string, checked: boolean) => {
    const next = new Set(selectedCustomRuleIds);
    if (checked) next.add(ruleId);
    else next.delete(ruleId);
    setSelectedCustomRuleIds(next);
  };
  // Category-level detectors have no rule list to customize; under the LLM
  // analyzer that includes `pii`, whose entity picker would be ignored.
  const customizeCategoryRules = (cat: RuleCategory) => {
    if (modeCategoryLevelDetectors.has(cat)) return;
    setCustomizeCategory(cat);
  };

  // Build the full update/create body, mirroring PolicyCenter's standard branch.
  const save = (options?: { supersedeDecisions?: boolean }) => {
    const {
      sources,
      presidioEntities,
      promptInjectionRules,
      disabledRules: payloadDisabled,
    } = categoriesToPayload(
      selectedCategories,
      disabledRules,
      pinnedHiddenRuleIds(policy?.presidioEntities),
      mode,
    );
    // Under the LLM analyzer the entity list is not what runs, so an edit
    // echoes the stored list back unchanged rather than the empty list the
    // collapsed PII card would derive: turning the flag off must restore the
    // policy exactly as it was. Only while PII stays on, though: once the
    // presidio source is gone the entities go with it, or a flag-off would
    // read them back as selected detectors. A create has nothing stored.
    const updatePresidioEntities =
      mode === "llm" && sources.includes("presidio")
        ? (policy?.presidioEntities ?? [])
        : presidioEntities;
    // Flag-only sources (destructive_tool, cli_destructive, account_identity)
    // are rejected by the server with action=block, so force flag as a safety
    // net in case the form state drifted.
    const flagOnlyActive = sources.some((s) =>
      FLAG_ONLY_CATEGORIES.has(s as RuleCategory),
    );
    const resolvedAction =
      flagOnlyActive && action !== "flag" ? "flag" : action;
    const principals =
      audienceType === "targeted" ? [...audiencePrincipalUrns] : [];
    const autoName = name.trim() === "";
    // Only send approved domains while the Non-Corporate Accounts category is
    // selected (an empty array clears them); omit otherwise so the server
    // preserves whatever is stored.
    const identityActive = selectedCategories.has("account_identity");
    const approvedEmailDomains = parseApprovedEmailDomains(approvedDomains);
    const detectionScopes = detectionScopesPayload(
      selectedCategories,
      scopeOverrides,
      mode,
    );
    const shadowMcpAllowedUrls = shadowMCPAllowedURLsForMutation({
      action: resolvedAction,
      selectedCategories,
      selectedURLs: selectedShadowMCPURLs,
      originalPolicy: policy,
      disposition: shadowMCPDisposition,
    });
    const shadowMcpBlockedUrls = shadowMCPBlockedURLsForMutation({
      action: resolvedAction,
      selectedCategories,
      selectedURLs: selectedShadowMCPURLs,
      disposition: shadowMCPDisposition,
    });
    const setupFields = {
      ...(shadowMcpAllowedUrls === undefined ? {} : { shadowMcpAllowedUrls }),
      ...(shadowMcpBlockedUrls === undefined ? {} : { shadowMcpBlockedUrls }),
    };

    if (policy) {
      // A URL toggle that contradicts a recorded decision needs explicit
      // confirmation before it supersedes that decision.
      if (
        !options?.supersedeDecisions &&
        targetIsShadowMCPBlock &&
        originalHasShadowMCPBlockConfiguration
      ) {
        const conflicts = shadowMCPDecisionConflicts({
          servers: inventoryQuery.data ?? [],
          originalURLs: originalShadowMCPURLs,
          selectedURLs: selectedShadowMCPURLs,
          disposition: shadowMCPDisposition,
        });
        if (conflicts.length > 0) {
          setSupersedeConflicts(conflicts);
          return;
        }
      }
      updateMutation.mutate({
        request: {
          updateRiskPolicyRequestBody: {
            id: policy.id,
            name: name.trim() || policy.name,
            sources,
            presidioEntities: updatePresidioEntities,
            promptInjectionRules,
            detectionScopes,
            mcpScope: mcpScopePayload ?? { servers: [] },
            disabledRules: payloadDisabled,
            customRuleIds: [...selectedCustomRuleIds],
            action: resolvedAction,
            audienceType,
            audiencePrincipalUrns: principals,
            autoName,
            userMessage,
            score,
            // Always send under the legacy engine: default when no Presidio
            // category is active, so disabling them resets the stored
            // threshold instead of leaving a stale value that would resurface
            // if Presidio is re-enabled later (update omits preserve prior
            // values server-side). Under the LLM analyzer the field is
            // omitted so the stored value stays untouched for a flag-off.
            ...(mode === "presidio"
              ? {
                  presidioScoreThreshold: presidioActive
                    ? presidioThreshold
                    : DEFAULT_PRESIDIO_THRESHOLD,
                }
              : {}),
            ...setupFields,
            ...(options?.supersedeDecisions
              ? { supersedeDecisions: true }
              : {}),
            ...(identityActive ? { approvedEmailDomains } : {}),
          },
        },
      });
    } else {
      createMutation.mutate({
        request: {
          createRiskPolicyRequestBody: {
            ...(autoName ? {} : { name: name.trim() }),
            enabled: true,
            sources,
            presidioEntities,
            promptInjectionRules,
            ...(detectionScopes.length > 0 ? { detectionScopes } : {}),
            ...(mcpScopePayload === null ? {} : { mcpScope: mcpScopePayload }),
            disabledRules: payloadDisabled,
            customRuleIds: [...selectedCustomRuleIds],
            action: resolvedAction,
            audienceType,
            audiencePrincipalUrns: principals,
            autoName,
            ...(userMessage.trim() ? { userMessage } : {}),
            score,
            ...(presidioActive
              ? { presidioScoreThreshold: presidioThreshold }
              : {}),
            ...setupFields,
            // The disposition only exists on blocking shadow MCP policies and
            // is immutable after create, so it is never sent on update.
            ...(isBlockingShadowMCPPolicy(true, sources, resolvedAction)
              ? { shadowMcpDisposition: shadowMCPDisposition }
              : {}),
            ...(identityActive ? { approvedEmailDomains } : {}),
          },
        },
      });
    }
  };

  const categoryCards = allCategories(mode);

  const header = (
    <PolicyHeader
      kind="standard"
      policy={policy}
      name={name}
      onNameChange={setName}
      dirty={dirty}
      saving={saving}
      actionDisabled={saveBlocked}
      onSubmit={() => save()}
      onCreate={() => save()}
    />
  );

  return (
    <>
      <SupersedeDecisionsDialog
        conflicts={supersedeConflicts}
        saving={saving}
        onCancel={() => setSupersedeConflicts(null)}
        onConfirm={() => save({ supersedeDecisions: true })}
      />
      <StepperShell
        header={header}
        provenance={
          draft ? (
            <PolicyDraftProvenance draft={draft}>
              {approvedDomainsRequired ? (
                <ApprovedDomainsRequirement
                  value={approvedDomains}
                  onChange={setApprovedDomains}
                  invalid={invalidApprovedDomains}
                />
              ) : null}
            </PolicyDraftProvenance>
          ) : null
        }
        steps={STANDARD_STEPS}
        current={step}
        onStep={setStep}
        prefilledStepIds={draft?.origin ? STANDARD_PREFILLED_STEPS : undefined}
      >
        {step === 0 && (
          <>
            <Card>
              <SectionHeader description="Turn on detector categories and attach your organization's custom rules." />
              <Stack gap={5}>
                {llmAnalyzerActive && <LlmAnalyzerNotice />}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">
                      Built-in rules
                    </Label>
                    <span className="text-muted-foreground text-xs">
                      {
                        categoryCards.filter((c) => selectedCategories.has(c))
                          .length
                      }{" "}
                      on
                    </span>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {categoryCards.map((cat) => (
                      <DetectorCard
                        key={cat}
                        category={cat}
                        selected={selectedCategories.has(cat)}
                        disabledRules={disabledRules}
                        disabledReason={
                          allowAllPostureLocked && cat === "shadow_mcp"
                            ? SHADOW_MCP_ALLOW_ALL_LOCK_REASON
                            : builtInRuleDisabledReason(cat, selectedCategories)
                        }
                        mode={mode}
                        fromUseCase={draftCategories.has(cat)}
                        onToggle={(checked) => toggleCategory(cat, checked)}
                        onCustomize={() => customizeCategoryRules(cat)}
                      />
                    ))}
                  </div>
                </div>
                {customRules.length > 0 && (
                  <RuleSelectList
                    title="Custom Rules"
                    description={
                      <>
                        Attach your organization's custom rules as{" "}
                        <span className="text-foreground font-medium">
                          detectors
                        </span>
                        . A match records a finding.
                      </>
                    }
                    idPrefix="detector"
                    customRules={customRules}
                    selectedRuleIds={selectedCustomRuleIds}
                    onToggleRule={toggleDetector}
                    expanded={detectionExpanded}
                    onToggle={() => setDetectionExpanded((v) => !v)}
                  />
                )}
                {!hasEnabledDetector && (
                  <Text small className="text-destructive">
                    Turn on at least one detector or attach a custom rule.
                  </Text>
                )}
              </Stack>
            </Card>
            {presidioActive && (
              <SensitivitySection
                threshold={presidioThreshold}
                setThreshold={setPresidioThreshold}
              />
            )}
          </>
        )}

        {step === 1 && (
          <ScopeStep
            selectedCategories={selectedCategories}
            scopeOverrides={scopeOverrides}
            setScopeOverrides={setScopeOverrides}
            mcpScope={mcpScope}
            setMcpScope={updateMCPScope}
            action={action}
            hasStoredMcpScope={!!policy?.mcpScope}
            draftScopeNames={draftScopeNames}
            onClearDraftScope={() => {
              setDraftScopeNames([]);
              updateMCPScope(initialPolicyMCPScopeValue(null));
            }}
          />
        )}

        {step === 2 && (
          <ActionStep
            action={action}
            setAction={setAction}
            audienceType={audienceType}
            setAudienceType={setAudienceType}
            audiencePrincipalUrns={audiencePrincipalUrns}
            setAudiencePrincipalUrns={setAudiencePrincipalUrns}
            userMessage={userMessage}
            setUserMessage={setUserMessage}
            score={score}
            setScore={setScore}
            flagOnlySelected={flagOnlySelected}
            mcpScoped={mcpScope.mode === "mcp"}
            pinnedAction={
              allowAllPostureLocked
                ? {
                    value: "block",
                    reason: SHADOW_MCP_ALLOW_ALL_LOCK_REASON,
                  }
                : undefined
            }
            shadowMCPAllowedServers={
              targetIsShadowMCPBlock ? (
                <div className="grid gap-5 lg:grid-cols-3 lg:items-start">
                  <ShadowMCPDispositionPicker
                    value={shadowMCPDisposition}
                    onChange={(next) => {
                      if (next === shadowMCPDisposition) return;
                      // The URL set's meaning flips with the disposition
                      // (allowed vs blocked servers), so a stale selection
                      // must not carry across.
                      setShadowMCPDisposition(next);
                      setSelectedShadowMCPURLs(new Set());
                    }}
                    readOnly={policy !== null}
                  />
                  <div className="lg:col-span-2">
                    <ShadowMCPPolicyServerSelector
                      servers={inventoryQuery.data ?? []}
                      originalURLs={
                        originalShadowMCPURLs ?? EMPTY_SHADOW_MCP_URLS
                      }
                      selectedURLs={selectedShadowMCPURLs}
                      onSelectionChange={setSelectedShadowMCPURLs}
                      isLoading={inventoryQuery.isPending}
                      error={inventoryQuery.error}
                      onRetry={() => void inventoryQuery.refetch()}
                      mode={
                        shadowMCPDisposition === "allow_all" ? "block" : "allow"
                      }
                    />
                  </div>
                </div>
              ) : undefined
            }
          />
        )}

        {step === 3 && (
          <StandardReview
            name={name}
            categories={selectedCategories}
            customRuleCount={selectedCustomRuleIds.size}
            mcpScope={mcpScope}
            customizedScopeCount={scopeOverrides.size}
            action={action}
            score={score}
            presidioActive={presidioActive}
            presidioThreshold={presidioThreshold}
            mode={mode}
            audienceType={audienceType}
            audiencePrincipalCount={audiencePrincipalUrns.size}
          />
        )}

        <StepNav
          step={step}
          count={STANDARD_STEPS.length}
          onStep={setStep}
          continueDisabled={step === 1 && !policyMCPScopeComplete(mcpScope)}
        />
      </StepperShell>

      {customizeCategory && (
        <CustomizeRulesSheet
          category={customizeCategory}
          selectedCategories={selectedCategories}
          setSelectedCategories={setSelectedCategories}
          disabledRules={disabledRules}
          setDisabledRules={setDisabledRules}
          approvedDomains={approvedDomains}
          setApprovedDomains={setApprovedDomains}
          onClose={() => setCustomizeCategory(null)}
        />
      )}
    </>
  );
}

// Read-only recap of the standard policy configuration on the Review step.
function StandardReview({
  name,
  categories,
  customRuleCount,
  mcpScope,
  customizedScopeCount,
  action,
  score,
  presidioActive,
  presidioThreshold,
  mode,
  audienceType,
  audiencePrincipalCount,
}: {
  name: string;
  categories: Set<RuleCategory>;
  customRuleCount: number;
  mcpScope: PolicyMCPScopeValue;
  customizedScopeCount: number;
  action: PolicyAction;
  score: number;
  presidioActive: boolean;
  presidioThreshold: number;
  mode: DetectorMode;
  audienceType: "everyone" | "targeted";
  audiencePrincipalCount: number;
}): JSX.Element {
  const detectorLabels = [...categories]
    .filter((c) => c !== "custom")
    .map((c) => ruleCategoryMeta(c, mode).label);
  if (customRuleCount > 0) {
    detectorLabels.push(
      `${customRuleCount} custom rule${customRuleCount === 1 ? "" : "s"}`,
    );
  }

  const scopeText = scopeSummaryText(mcpScope, customizedScopeCount);

  return (
    <Card>
      <SectionHeader description="Confirm the configuration before creating the policy." />
      <Stack gap={4}>
        <SummaryRow label="Name">
          <Text small>{name.trim() || "Auto-generated from detectors"}</Text>
        </SummaryRow>
        <SummaryRow label="Detectors">
          {detectorLabels.length > 0 ? (
            <div className="flex flex-wrap justify-end gap-1.5">
              {detectorLabels.map((l) => (
                <Badge key={l} variant="neutral">
                  {l}
                </Badge>
              ))}
            </div>
          ) : (
            <Text small muted>
              None selected
            </Text>
          )}
        </SummaryRow>
        {presidioActive ? (
          <SummaryRow label="Sensitivity">
            <Text small mono>
              {presidioThreshold.toFixed(2)}
              {presidioThreshold === DEFAULT_PRESIDIO_THRESHOLD
                ? " · default"
                : ""}
            </Text>
          </SummaryRow>
        ) : null}
        <SummaryRow label="Scope">
          <Text small className="text-right">
            {scopeText}
          </Text>
        </SummaryRow>
        <SummaryRow label="Action">
          <Badge variant={action === "flag" ? "neutral" : "warning"}>
            {POLICY_ACTION_LABEL[action]}
          </Badge>
        </SummaryRow>
        <SummaryRow label="Severity">
          <SeverityBadge score={score} />
        </SummaryRow>
        <SummaryRow label="Audience">
          <Text small>
            {audienceType === "targeted"
              ? `${audiencePrincipalCount} targeted principal${
                  audiencePrincipalCount === 1 ? "" : "s"
                }`
              : "Everyone"}
          </Text>
        </SummaryRow>
      </Stack>
    </Card>
  );
}

// ── helpers ─────────────────────────────────────────────────────────────────

function sameMCPScope(a: RiskMCPScope | null, b: RiskMCPScope | null): boolean {
  const normalize = (scope: RiskMCPScope | null) => {
    if (scope === null) return null;
    return {
      allServers: scope.allServers ?? false,
      toolAnnotations: [...(scope.toolAnnotations ?? [])].sort(),
      servers: scope.servers
        .map((server) => ({
          id: server.mcpServerId,
          tools: server.tools === undefined ? null : [...server.tools].sort(),
        }))
        .sort((left, right) => left.id.localeCompare(right.id)),
    };
  };
  return JSON.stringify(normalize(a)) === JSON.stringify(normalize(b));
}

function sameSet<T>(a: Set<T>, b: Set<T>): boolean {
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
}

function formatRelative(date: Date): string {
  const secs = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (secs < 60) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatEvalConfidence(confidence: number): string {
  return `${Math.round(confidence * 100)}%`;
}

function formatEvalLatency(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "0ms";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(ms < 10_000 ? 1 : 0)}s`;
}

function formatEvalCostLatency(costUsd: number, latencyMs: number): string {
  return `${formatUsageCost(costUsd)} eval · ${formatEvalLatency(latencyMs)}`;
}
